// src/pages/Candidates.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { approveCandidate, checkAadhar } from "../../Api/candidateApi";
import { extractAadhaarFromImage, extractDlFromImage } from "../utils/clientOcrParser";
import { FaCamera, FaVideoSlash, FaCheckCircle, FaExclamationTriangle, FaCalendarCheck, FaIdCard, FaCar } from "react-icons/fa";
import { BiShow, BiEdit, BiTrash } from "react-icons/bi";
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

import {
  getAllCandidates,
  createCandidate,
  updateCandidate,
  deleteCandidate,
} from "../../Api/candidateApi";
import { getAllProjects, getProgramsByProject} from "../../Api/projectApi";
import { getAllBranches, getMyBranch } from "../../Api/branches";
import toast from "react-hot-toast";
//  import imageCompression from "browser-image-compression";
 import {
  IdentificationIcon,
  DocumentArrowDownIcon,
  CameraIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { logClientAction } from "../../Api/logApi";
import { uploadCandidateCsv } from "../../Api/excelApi";
import ViewCandidateModal from "./ViewCandidateModal";
import ExportCandidatesDialog from "./ExportCandidatesDialog";

/* =====================================================================
   DESIGN TOKENS — presentation-only helpers. Nothing here touches
   state, handlers, API calls or validation logic below.
===================================================================== */
const ui = {
  card:
    "bg-white border border-slate-200/70 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
  input:
    "bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-[13.5px] text-slate-800 placeholder-slate-400 shadow-[0_1px_2px_rgba(15,23,42,0.03)] outline-none transition focus:border-[#2BB5CE] focus:ring-2 focus:ring-[#2BB5CE]/15",
  select:
    "bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-[13.5px] text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.03)] outline-none transition focus:border-[#2BB5CE] focus:ring-2 focus:ring-[#2BB5CE]/15 cursor-pointer",
  label: "text-[12.5px] font-semibold text-slate-600 mb-1.5 tracking-tight",
  pillBtnBase:
    "flex items-center gap-2 h-[42px] px-5 rounded-xl text-[13.5px] font-semibold shadow-sm transition whitespace-nowrap active:scale-[0.98]",
  pillBtnDark:
    "flex items-center gap-2 h-[42px] px-5 rounded-xl text-[13.5px] font-semibold shadow-sm transition whitespace-nowrap active:scale-[0.98] bg-[#2BB5CE] text-white hover:bg-[#1A869B]",
  pillBtnPrimary:
    "flex items-center gap-2 h-[42px] px-5 rounded-xl text-[13.5px] font-semibold shadow-sm transition whitespace-nowrap active:scale-[0.98] bg-[#2BB5CE] text-white hover:bg-[#1A869B]",
  pillBtnPurple:
    "flex items-center gap-2 h-[42px] px-5 rounded-xl text-[13.5px] font-semibold shadow-sm transition whitespace-nowrap active:scale-[0.98] bg-slate-900 text-white hover:bg-slate-800",
  pillBtnSuccess:
    "flex items-center gap-2 h-[42px] px-5 rounded-xl text-[13.5px] font-semibold shadow-sm transition whitespace-nowrap active:scale-[0.98] bg-[#2BB5CE] text-white hover:bg-[#1A869B]",
  iconBtn:
    "p-2 rounded-lg bg-white border border-slate-200 shadow-sm hover:shadow transition",
  badge: "px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide",
  sectionTitle:
    "text-[11px] font-bold uppercase tracking-wider text-slate-400",
};

/* ── Section accent color palette — one per SectionCard ── */
const SECTION_META = [
  { id: 'sec-personal', label: 'Personal Details',      icon: '👤', color: '#6366F1', ring: 'rgba(99,102,241,0.12)',  bgLight: 'bg-indigo-50',  textDark: 'text-indigo-600',  borderCls: 'border-l-indigo-400', pillBg: 'bg-indigo-50',  pillText: 'text-indigo-700', completeBg: 'bg-indigo-50/40' },
  { id: 'sec-identity', label: 'Identity & Contact',    icon: '🪪', color: '#2BB5CE', ring: 'rgba(43,181,206,0.12)',  bgLight: 'bg-teal-50',    textDark: 'text-teal-600',    borderCls: 'border-l-teal-400',   pillBg: 'bg-teal-50',    pillText: 'text-teal-700',   completeBg: 'bg-teal-50/40'   },
  { id: 'sec-dl',       label: 'Driving Licence',       icon: '🚗', color: '#F59E0B', ring: 'rgba(245,158,11,0.12)',  bgLight: 'bg-amber-50',   textDark: 'text-amber-600',   borderCls: 'border-l-amber-400',  pillBg: 'bg-amber-50',   pillText: 'text-amber-700',  completeBg: 'bg-amber-50/40'  },
  { id: 'sec-edu',      label: 'Education & Employment', icon: '🎓', color: '#8B5CF6', ring: 'rgba(139,92,246,0.12)', bgLight: 'bg-violet-50',  textDark: 'text-violet-600',  borderCls: 'border-l-violet-400', pillBg: 'bg-violet-50',  pillText: 'text-violet-700', completeBg: 'bg-violet-50/40' },
  { id: 'sec-program',  label: 'Program & Location',    icon: '📍', color: '#0EA5E9', ring: 'rgba(14,165,233,0.12)',  bgLight: 'bg-sky-50',     textDark: 'text-sky-600',     borderCls: 'border-l-sky-400',    pillBg: 'bg-sky-50',     pillText: 'text-sky-700',    completeBg: 'bg-sky-50/40'    },
  { id: 'sec-docs',     label: 'Documents',             icon: '📄', color: '#10B981', ring: 'rgba(16,185,129,0.12)', bgLight: 'bg-emerald-50', textDark: 'text-emerald-600', borderCls: 'border-l-emerald-400',pillBg: 'bg-emerald-50', pillText: 'text-emerald-700',completeBg: 'bg-emerald-50/40'},
  { id: 'sec-admin',    label: 'Office Use Only',       icon: '🏢', color: '#64748B', ring: 'rgba(100,116,139,0.12)',bgLight: 'bg-slate-100',  textDark: 'text-slate-500',   borderCls: 'border-l-slate-300',  pillBg: 'bg-slate-100',  pillText: 'text-slate-600',  completeBg: 'bg-slate-50/40'  },
];

const blobToFile = (blob, fileName) => {
  if (!blob) return null;
  if (blob instanceof File) {
    return blob;
  }
  return new File(
    [blob],
    fileName,
    {
      type: blob.type || "image/jpeg",
    }
  );
};

const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const enrollFaceOnBackend = async (candidateId, base64Image) => {
  const OCR_API_URL = process.env.REACT_APP_OCR_API_URL || "https://ocr.eduforestin.com";
  const file = dataURLtoFile(base64Image, "face.jpg");
  
  const ocrFormData = new FormData();
  ocrFormData.append("candidateId", candidateId);
  ocrFormData.append("image", file);
  
  const response = await fetch(`${OCR_API_URL}/v1/face/enroll`, {
    method: "POST",
    headers: {
      "x-internal-key": "dev-ocr-internal-key",
    },
    body: ocrFormData,
  });
  
  if (!response.ok) {
    throw new Error(`Enrollment server returned status ${response.status}`);
  }
  
  const result = await response.json();
  if (!result.success) {
    throw new Error(result.message || "Face enrollment failed");
  }
  return result;
};

const emptyCandidate = {
  project: "",
  program:"",
  location: "",
  // status: "",
  batchId: "",
  name: "",
  fathersName: "",
  mothersName: "",
  maritalStatus: "",
  caste: "",
  address: "",
  aadharNumber: "",
  dob: "",
  gender: "",
  religion: "",
  religionOther: "",

  vulnerability: "",
  annualIncome: "",
  qualification: "",
  contactNumber: "",
  assessmentDate: (() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  })(),
  dlNo: "",
  dlType: "",
  licenseExpiryDate: "",
  dependentFamilyMembers: "",
  ownerOrDriver: "",
  abha: "",
  result: "",
  certificateNo: "",
  remarks: "",
  ekycRemarks: "",
  ekycRegisteredEmail: "",
  barCode: "",
  dlIssueDate: "",
  dlAuthority: "",
  experienceYears: "",
  employerName: "",
  employerAddress: "",

  // ⭐ NEW FILE UPLOAD FIELDS
  aadharFile: "",
  aadharBackFile: "",
  dlFile: "",
  dlBackFile: "",
  otherFile: "",
};

// 1️⃣ Fields that MUST come first (your required order)
const primaryOrderedFields = [
  "aadharNumber",

  "name",
  "fathersName",
  "mothersName",
  "dob",
  "maritalStatus",
  "religion",
  "caste",
  "annualIncome",
  "address",
  "dlNo",
  "dlIssueDate",
  "licenseExpiryDate",
  "dlType",
  "dlAuthority",
  "contactNumber",
  "qualification",
  "experienceYears",
  "employerName",
  "employerAddress",
  "location",
];

// 2️⃣ Uploads MUST come last
const uploadFields = ["aadharFile", "aadharBackFile", "dlFile", "dlBackFile", "otherFile"];

// 3️⃣ Fields already rendered separately
const excludedFields = [
  ...primaryOrderedFields,
  ...uploadFields,
  "project",
  "program",
  ""
];

// 4️⃣ Automatically include ALL remaining fields
const remainingFields = Object.keys(emptyCandidate).filter(
  (f) => !excludedFields.includes(f)
);

// ✅ FINAL ORDER (THIS IS WHAT YOU MAP)
const finalFieldOrder = [
  ...primaryOrderedFields,
  ...remainingFields,
  ...uploadFields,
];


  const Candidates = () => {
    const navigate = useNavigate();
    const location = useLocation();



    const [data, setData] = useState([]);
    const [loadingTable, setLoadingTable] = useState(false);
const role = localStorage.getItem("role"); // "admin" | "branchUser"

    // Drawer & form
    const [openDrawer, setOpenDrawer] = useState(false);
    const [form, setForm] = useState(emptyCandidate);
    const [editingId, setEditingId] = useState(null);
    const [registrationStartedAt, setRegistrationStartedAt] = useState(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
// const [myBranchLocation, setMyBranchLocation] = useState("");

    // Delete
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    // View popup
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [facePreviewUrl, setFacePreviewUrl] = useState(null);
    
    // New state to store unique locations
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
const [projects, setProjects] = useState([]);
const [programs, setPrograms] = useState([]);
const [exportDialogOpen, setExportDialogOpen] = useState(false);
const [exportType, setExportType] = useState("excel"); // "excel" | "pdf"
const [createCandidateDialogOpen, setCreateCandidateDialogOpen] = useState(false);
const [scanDialog, setScanDialog] = useState(false);
const [scanImage, setScanImage] = useState(null);
const [scanning, setScanning] = useState(false);

// ⭐ Aadhaar + DL multi-step scan flow
const [scanStep, setScanStep] = useState("aadhar"); // "aadhar" | "dl" | "review"
const [dlScanImage, setDlScanImage] = useState(null);
const [extracting, setExtracting] = useState(false);
const [extractedAadharData, setExtractedAadharData] = useState(null);
const [extractedDlData, setExtractedDlData] = useState(null);
const [aadharRawText, setAadharRawText] = useState("");
const [dlRawText, setDlRawText] = useState("");
const optionalFields = ["otherFile", "aadharBackFile", "dlBackFile"];  
const [aadharDialogOpen, setAadharDialogOpen] = useState(false);
// const [aadharCheckStatus, setAadharCheckStatus] = useState(""); 
 
const [checkAadharValue, setCheckAadharValue] = useState("");
const [aadharCheckStatus, setAadharCheckStatus] = useState(""); // checking | exists | new

const [branchLocations, setBranchLocations] = useState([]);
const videoRef = React.useRef(null);
const canvasRef = React.useRef(null);
const [aadharStatus, setAadharStatus] = useState(null);
 
const [abhaError, setAbhaError] = useState("");
const [contactError, setContactError] = useState("");
const [saving, setSaving] = useState(false);

const [cameraOpen, setCameraOpen] = useState(false);
const [cameraStream, setCameraStream] = useState(null);
const [approvalDialog, setApprovalDialog] = useState(false);
const [approvalStatus, setApprovalStatus] = useState("");
const [approvalReason, setApprovalReason] = useState("");
const [approvalCandidate, setApprovalCandidate] = useState(null);
const [confirmCreateDialog, setConfirmCreateDialog] = useState(false);
const [ocrCandidateData, setOcrCandidateData] = useState(null);
const [csvDialogOpen, setCsvDialogOpen] = useState(false);
const [faceCameraOpen, setFaceCameraOpen] = useState(false);
const [faceCameraStream, setFaceCameraStream] = useState(null);
const [capturedFaceImage, setCapturedFaceImage] = useState(null);
const [capturedFaceDescriptor, setCapturedFaceDescriptor] = useState(null);
const [faceScanning, setFaceScanning] = useState(false);
const [rawProjectsList, setRawProjectsList] = useState([]);
const [attendanceLogOpen, setAttendanceLogOpen] = useState(false);
const [selectedLogCandidate, setSelectedLogCandidate] = useState(null);
const [selectedLogRecords, setSelectedLogRecords] = useState([]);
const [faceScanProgress, setFaceScanProgress] = useState(0);
const [liveVerificationResult, setLiveVerificationResult] = useState(null);
const [activeSection, setActiveSection] = useState('sec-personal');
const scrollBodyRef = React.useRef(null);
const [aadharPreview, setAadharPreview] = useState(null);
const [aadharBackPreview, setAadharBackPreview] = useState(null);
const [dlPreview, setDlPreview] = useState(null);
const [dlBackPreview, setDlBackPreview] = useState(null);
const [csvFile, setCsvFile] = useState(null);
const [csvUploading, setCsvUploading] = useState(false);
const [csvResult, setCsvResult] = useState(null);
const [csvPreview, setCsvPreview] = useState([]);
const requiredCsvHeaders = ["Project", "Aadharno"];

const safeLogClientAction = (action, module, metadata = {}) => {
  logClientAction(action, module, metadata).catch((err) => {
    console.warn("Client log failed:", err.response?.status || err.message);
  });
};

const getCsvHeaders = (text) => {
  const headerLine = text.split(/\r?\n/).find((line) => line.trim());
  if (!headerLine) return [];

  return headerLine
    .split(",")
    .map((header) => header.trim().replace(/^"|"$/g, ""));
};

const handleCsvFileChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setCsvResult(null);
  setCsvPreview([]);

  if (!file.name.toLowerCase().endsWith(".csv")) {
    toast.error("Please select a valid CSV file");
    e.target.value = "";
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    toast.error("File size must be less than 5MB");
    e.target.value = "";
    return;
  }  

  const reader = new FileReader();
  reader.onload = (event) => {
    const text = event.target.result || "";
    const headers = getCsvHeaders(text);
    const missingHeaders = requiredCsvHeaders.filter(
      (header) => !headers.includes(header)
    );

    if (missingHeaders.length) {
      toast.error(`Missing CSV column: ${missingHeaders.join(", ")}`);
      setCsvFile(null);
      e.target.value = "";
      return;
    }

    const rows = text.split(/\r?\n/).filter(Boolean).slice(0, 6);
    setCsvFile(file);
    setCsvPreview(rows);
  };
  reader.onerror = () => {
    toast.error("Could not read CSV file");
    setCsvFile(null);
    e.target.value = "";
  };
  reader.readAsText(file);
};

const handleCsvUpload = async () => {
  if (!csvFile) {
    toast.error('Please select a CSV file');
    return;
  }

  try {
    setCsvUploading(true);
    toast.loading("Uploading CSV...", { id: "csv-upload" });

    const res = await uploadCandidateCsv(csvFile);

    setCsvResult(res.data);
    toast.success(res.data?.message || "CSV uploaded successfully", {
      id: "csv-upload",
    });
    setPage(0);
    await fetchCandidates();

  } catch (err) {
    console.error(err);
    const errorMsg =
      err.response?.status === 403
        ? "You do not have permission to upload CSV. Please check backend role access for this account."
        : err.response?.data?.message || "Upload failed";
    toast.error(errorMsg, { id: "csv-upload" });
  } finally {
    setCsvUploading(false);
  }
};

const resetCsvUpload = () => {
  setCsvFile(null);
  setCsvPreview([]);
  setCsvResult(null);
  setCsvDialogOpen(false);
};
  useEffect(() => {
    fetchCandidates();
  }, [page, rowsPerPage, selectedLocation, search]);
  
useEffect(() => {
  const fetchLocations = async () => {
    try {
      const res = await getAllBranches();

      // ✅ IMPORTANT: access res.data.data
      const locations = [
        ...new Set(
          res.data.data
            .map((branch) => branch.location?.trim())
            .filter(Boolean)
        ),
      ];

      setBranchLocations(locations);
    } catch (error) {
      console.error("Failed to load locations", error);
    }
  };

  fetchLocations();
}, []);

// Load FaceAPI models on mount
useEffect(() => {
  const loadModels = async () => {
    try {
      if (window.faceapi) {
        await window.faceapi.nets.ssdMobilenetv1.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
        await window.faceapi.nets.faceLandmark68Net.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
        await window.faceapi.nets.faceRecognitionNet.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
        console.log("FaceAPI models loaded in Candidates module");
      }
    } catch (err) {
      console.error("Failed to load FaceAPI models in Candidates:", err);
    }
  };
  loadModels();
}, []);

useEffect(() => {
  const fetchRawProjects = async () => {
    try {
      const res = await getAllProjects();
      if (res && res.data) {
        setRawProjectsList(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  };
  fetchRawProjects();
}, []);

 
useEffect(() => {
  const loadMyLocation = async () => {
    if (role !== "branchUser") return;

    try {
      const res = await getMyBranch();
      const branch = res.data;

      // 🔥 THIS IS THE KEY
      setSelectedLocation(branch.location);
    } catch (err) {
      console.error("Failed to load branch location", err);
    }
  };

  loadMyLocation();
}, [role]);

/* ── IntersectionObserver for section-jump rail ── */
useEffect(() => {
  if (!openDrawer) return;
  const container = scrollBodyRef.current;
  if (!container) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    },
    { root: container, threshold: 0.25 }
  );

  const timer = setTimeout(() => {
    SECTION_META.forEach(sec => {
      const el = container.querySelector(`#${sec.id}`);
      if (el) observer.observe(el);
    });
  }, 150);

  return () => {
    clearTimeout(timer);
    observer.disconnect();
  };
}, [openDrawer]);

/* ── useEffect hooks for Aadhaar and DL previews (robust against memory leaks and URL strings) ── */
useEffect(() => {
  if (!form.aadharFile) {
    setAadharPreview(null);
    return;
  }
  if (form.aadharFile instanceof Blob) {
    const url = URL.createObjectURL(form.aadharFile);
    setAadharPreview(url);
    return () => URL.revokeObjectURL(url);
  } else if (typeof form.aadharFile === "string") {
    setAadharPreview(form.aadharFile);
  } else {
    setAadharPreview(null);
  }
}, [form.aadharFile]);

useEffect(() => {
  if (!form.aadharBackFile) {
    setAadharBackPreview(null);
    return;
  }
  if (form.aadharBackFile instanceof Blob) {
    const url = URL.createObjectURL(form.aadharBackFile);
    setAadharBackPreview(url);
    return () => URL.revokeObjectURL(url);
  } else if (typeof form.aadharBackFile === "string") {
    setAadharBackPreview(form.aadharBackFile);
  } else {
    setAadharBackPreview(null);
  }
}, [form.aadharBackFile]);

useEffect(() => {
  if (!form.dlFile) {
    setDlPreview(null);
    return;
  }
  if (form.dlFile instanceof Blob) {
    const url = URL.createObjectURL(form.dlFile);
    setDlPreview(url);
    return () => URL.revokeObjectURL(url);
  } else if (typeof form.dlFile === "string") {
    setDlPreview(form.dlFile);
  } else {
    setDlPreview(null);
  }
}, [form.dlFile]);

useEffect(() => {
  if (!form.dlBackFile) {
    setDlBackPreview(null);
    return;
  }
  if (form.dlBackFile instanceof Blob) {
    const url = URL.createObjectURL(form.dlBackFile);
    setDlBackPreview(url);
    return () => URL.revokeObjectURL(url);
  } else if (typeof form.dlBackFile === "string") {
    setDlBackPreview(form.dlBackFile);
  } else {
    setDlBackPreview(null);
  }
}, [form.dlBackFile]);

  const fetchCandidates = async () => {
    try {
      setLoadingTable(true);

    const res = await getAllCandidates(
    page + 1,
    rowsPerPage,
    selectedLocation,
    search
  );


      setData(res.data.data);
      setTotal(res.data.total);

      // Extract unique locations from results (first time only)
      if (locations.length === 0) {
        const unique = [
          ...new Set(res.data.allLocations || res.data.data.map((c) => c.location)),
        ];
        setLocations(unique);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
    } finally {
      setLoadingTable(false);
    }
  };

    const handleChange = (key) => (e) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [key]: value }));
    };

const ensureISODateString = (val) => {
  if (!val) return "";
  if (typeof val !== "string") return "";
  // Check if it's already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  // If it's DD/MM/YYYY or DD-MM-YYYY
  const parts = val.split(/[\/\-]/);
  if (parts.length === 3) {
    let day = parts[0];
    let month = parts[1];
    let year = parts[2];
    
    // If year is 4 digits and day/month are 2 digits
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
    // If day is 4 digits (reverse split)
    if (day.length === 4 && month.length === 2 && year.length === 2) {
      return `${day}-${month}-${year}`;
    }
  }
  
  // Try to parse using JS Date
  try {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split("T")[0];
    }
  } catch (e) {}
  
  return "";
};

const openCreate = async (ocrData = null) => {
  const startTime = new Date().toISOString();
  setRegistrationStartedAt(startTime);
  safeLogClientAction("REGISTRATION_STARTED", "Candidate", { startedAt: startTime });

  setForm(emptyCandidate);
  setEditingId(null);
  setPrograms([]);
  setCapturedFaceImage(null);
  setCapturedFaceDescriptor(null);
  setLiveVerificationResult(null);
  setOpenDrawer(true);

  try {
    let defaultProject = "";
    let defaultProgram = "";

    // 🔑 ADMIN & SUPER ADMIN
    if (role === "admin" || role === "superAdmin") {
      const res = await getAllProjects();
      const uniqueProjects = [
        ...new Set(res.data.map((p) => p.projectName)),
      ];
      setProjects(uniqueProjects);
    }

    // 🔑 BRANCH USER
    else {
      const res = await getMyBranch(); // 👈 branch API
      const branch = res.data;

      const isMultiProject = Array.isArray(branch.project);
      let projList = [];

      if (isMultiProject) {
        projList = [...new Set(branch.project.map(item => item.project || item.projectName))].filter(Boolean);
        if (branch.project.length === 1) {
          defaultProject = branch.project[0].project || branch.project[0].projectName || "";
          defaultProgram = branch.project[0].program || branch.project[0].programName || "";
        }
      } else {
        projList = branch.project ? [branch.project] : [];
        defaultProject = branch.project || "";
        defaultProgram = branch.program || "";
      }
      setProjects(projList);

      setForm((prev) => ({
        ...prev,
        project: defaultProject,
        program: defaultProgram,
        location: branch.location,
      }));

      if (defaultProject) {
        if (isMultiProject) {
          const assignedProgs = branch.project
            .filter(item => (item.project || item.projectName) === defaultProject)
            .map(item => item.program || item.programName)
            .filter(Boolean);
          setPrograms(assignedProgs);
          if (assignedProgs.length === 1) {
            defaultProgram = assignedProgs[0];
            setForm((prev) => ({ ...prev, program: defaultProgram }));
          }
        } else {
          const progRes = await getProgramsByProject(defaultProject);
          const progs = progRes.data || [];
          setPrograms(progs);
          if (progs.length === 1) {
            defaultProgram = progs[0];
            setForm((prev) => ({ ...prev, program: defaultProgram }));
          }
        }
      } else {
        setPrograms([]);
      }
    }

    // If OCR data was pre-filled from document scanner, overlay candidate details onto form while retaining loaded projects & programs
    if (ocrData) {
      const cleanOcr = { ...ocrData };
      if (cleanOcr.dob) cleanOcr.dob = ensureISODateString(cleanOcr.dob);
      if (cleanOcr.dlIssueDate) cleanOcr.dlIssueDate = ensureISODateString(cleanOcr.dlIssueDate);
      if (cleanOcr.licenseExpiryDate) cleanOcr.licenseExpiryDate = ensureISODateString(cleanOcr.licenseExpiryDate);
      if (cleanOcr.assessmentDate) {
        cleanOcr.assessmentDate = ensureISODateString(cleanOcr.assessmentDate);
      }

      setForm((prev) => ({
        ...prev,
        ...cleanOcr,
        project: cleanOcr.project || prev.project || defaultProject || "",
        program: cleanOcr.program || prev.program || defaultProgram || "",
      }));
    }
  } catch (error) {
    console.error("Load error:", error);
  }
};

useEffect(() => {
  const ocrDataStr = localStorage.getItem("ocrCandidateData");
  if (ocrDataStr) {
    try {
      const parsedData = JSON.parse(ocrDataStr);
      if (location.state?.aadharFile) {
        parsedData.aadharFile = location.state.aadharFile;
      }
      if (location.state?.aadharBackFile) {
        parsedData.aadharBackFile = location.state.aadharBackFile;
      }
      if (location.state?.dlFile) {
        parsedData.dlFile = location.state.dlFile;
      }
      if (location.state?.dlBackFile) {
        parsedData.dlBackFile = location.state.dlBackFile;
      }
      openCreate(parsedData);
      toast.success("Loaded scanned candidate details into registration form!");
    } catch (err) {
      console.error("Error reading OCR data from storage:", err);
    } finally {
      localStorage.removeItem("ocrCandidateData");
    }
  }
}, [location.state]);



  const openEdit = async (candidate) => {
    console.log("EDIT CLICKED:", candidate._id);
  setEditingId(candidate._id);
  setCapturedFaceImage(candidate.biometricFace || null);
  setCapturedFaceDescriptor(null);
  if (candidate.faceMatchScore != null) {
    setLiveVerificationResult({
      score: candidate.faceMatchScore,
      verified: candidate.faceVerified,
      confidence: candidate.confidence || (candidate.faceMatchScore >= 80 ? "Medium" : "Low"),
      verifiedAt: candidate.verifiedAt
    });
  } else {
    setLiveVerificationResult(null);
  }
  setOpenDrawer(true);

  try {
    const savedAadharFace = candidate.aadharFace || localStorage.getItem(`dbskills_aadhar_face_${candidate._id}`) || "";
    const savedDlFace = candidate.dlFace || localStorage.getItem(`dbskills_dl_face_${candidate._id}`) || "";

    // preload form
    setForm({
      ...emptyCandidate,
      ...candidate,
      aadharFace: savedAadharFace,
      dlFace: savedDlFace
    });

    // ADMIN & SUPER ADMIN
    if (role === "admin" || role === "superAdmin") {
      const projRes = await getAllProjects();
      const uniqueProjects = [
        ...new Set(projRes.data.map((p) => p.projectName)),
      ];
      setProjects(uniqueProjects);

      if (candidate.project) {
        const progRes = await getProgramsByProject(candidate.project);
        setPrograms(progRes.data || []);
      }
    }

    // BRANCH USER
    else {
      const res = await getMyBranch();
      const branch = res.data;

      const isMultiProject = Array.isArray(branch.project);
      let projList = [];
      if (isMultiProject) {
        projList = [...new Set(branch.project.map(item => item.project || item.projectName))].filter(Boolean);
      } else {
        projList = branch.project ? [branch.project] : [];
      }
      setProjects(projList);

      setForm((prev) => ({
        ...prev,
        project: candidate.project || "",
        program: candidate.program || "",
        location: branch.location,
      }));

      if (candidate.project) {
        if (isMultiProject) {
          const assignedProgs = branch.project
            .filter(item => (item.project || item.projectName) === candidate.project)
            .map(item => item.program || item.programName)
            .filter(Boolean);
          setPrograms(assignedProgs);
        } else {
          const progRes = await getProgramsByProject(candidate.project);
          setPrograms(progRes.data || []);
        }
      } else {
        setPrograms([]);
      }
    }
  } catch (err) {
    console.error("Edit load failed", err);
    toast.error("Failed to load candidate for editing");
  }
};


 
// const remainingFields = Object.keys(emptyCandidate).filter(
//   (f) => !excludedFields.includes(f)
// );


 
const closeDrawer = () => {
  stopFaceCamera();
  setOpenDrawer(false);
  if (!editingId && registrationStartedAt) {
    const abandonedTime = new Date().toISOString();
    const duration = Date.now() - new Date(registrationStartedAt).getTime();
    safeLogClientAction("REGISTRATION_ABANDONED", "Candidate", {
      startedAt: registrationStartedAt,
      completedAt: abandonedTime,
      durationMs: duration,
      success: false
    });
    setRegistrationStartedAt(null);
  }
};
const saveCandidate = async () => {
  if (!validateForm()) return;

  if (!editingId && aadharStatus === "exists") {
    toast.error("Aadhaar already registered");
    return;
  }

  setSaving(true);
  setOpenDrawer(false);
  toast.loading("Saving candidate...", { id: "save" });

  try {
    const formData = new FormData();
    const systemFields = ["_id", "createdAt", "updatedAt", "__v"];

    for (const key in form) {
      const value = form[key];

      if (value === null || value === undefined || value === "") continue;
      if (systemFields.includes(key)) continue;

      // ✅ FILE FIELDS — send ONLY if new file selected
      if (["aadharFile", "aadharBackFile", "dlFile", "dlBackFile", "otherFile"].includes(key)) {
        if (value instanceof File) {
          formData.append(key, value); // 🚀 DIRECT FILE (NO COMPRESSION)
        }
        continue; // 🔒 DO NOT append string paths
      }

      // ✅ SKIP FACE & BIOMETRIC FIELDS (Handled explicitly below)
      if (["biometricFace", "aadharFace", "dlFace", "faceMatchScore", "faceVerified", "confidence", "verifiedAt"].includes(key)) {
        continue;
      }

      // ✅ BRANCH FIELD — extract string ID if populated object
      if (key === "branch") {
        const branchVal = typeof value === "object" && value?._id ? value._id : value;
        if (branchVal && branchVal !== "[object Object]") {
          formData.append("branch", branchVal);
        }
        continue;
      }

      // ✅ NORMAL PRIMITIVE FIELDS ONLY
      if (typeof value !== "object") {
        formData.append(key, value);
      }
    }

    const bioImg = capturedFaceImage || form.biometricFace;
    if (bioImg) {
      formData.append("biometricFace", bioImg);
    }
    if (form.aadharFace) {
      formData.append("aadharFace", form.aadharFace);
    }
    if (form.dlFace) {
      formData.append("dlFace", form.dlFace);
    }

    // const bioImg = capturedFaceImage || form.biometricFace;
    const docFace = form.aadharFace || form.dlFace;
    if (bioImg && docFace) {
      const matchRes = await compareFacesAndCalculateScore(bioImg, docFace);
      if (matchRes) {
        formData.append("faceMatchScore", matchRes.score);
        formData.append("faceVerified", matchRes.verified);
        formData.append("confidence", matchRes.confidence);
        formData.append("verifiedAt", matchRes.verifiedAt);
      }
    }

    if (editingId) {
      await updateCandidate(editingId, formData);
      if (capturedFaceImage) {
        toast.loading("Enrolling biometric face template...", { id: "save" });
        try {
          await enrollFaceOnBackend(editingId, capturedFaceImage);
        } catch (enrollErr) {
          console.error("Biometric re-enrollment failed:", enrollErr);
          let userMsg = "Biometric enrollment failed. Please try again.";
          if (enrollErr.message.includes("No face detected")) {
            userMsg = "No face detected. Please position the candidate correctly.";
          } else if (enrollErr.message.includes("Multiple faces")) {
            userMsg = "Multiple faces detected. Please make sure only the candidate is visible.";
          } else if (enrollErr.message.includes("Failed to connect") || enrollErr.message.includes("Fetch")) {
            userMsg = "Unable to connect to biometric service.";
          } else {
            userMsg = `Enrollment error: ${enrollErr.message}`;
          }
          toast.error(userMsg, { id: "save" });
          setSaving(false);
          return;
        }
      }
      if (form.aadharFace) localStorage.setItem(`dbskills_aadhar_face_${editingId}`, form.aadharFace);
      if (form.dlFace) localStorage.setItem(`dbskills_dl_face_${editingId}`, form.dlFace);
    } else {
      const res = await createCandidate(formData);
      const newId = res.data?.candidate?._id;
      if (res.data?.success && newId) {
        if (capturedFaceImage) {
          toast.loading("Enrolling biometric face template...", { id: "save" });
          try {
            await enrollFaceOnBackend(newId, capturedFaceImage);
          } catch (enrollErr) {
            console.error("Biometric enrollment failed:", enrollErr);
            let userMsg = "Biometric enrollment failed. Please try again.";
            if (enrollErr.message.includes("No face detected")) {
              userMsg = "No face detected. Please position the candidate correctly.";
            } else if (enrollErr.message.includes("Multiple faces")) {
              userMsg = "Multiple faces detected. Please make sure only the candidate is visible.";
            } else if (enrollErr.message.includes("Failed to connect") || enrollErr.message.includes("Fetch")) {
              userMsg = "Unable to connect to biometric service.";
            } else {
              userMsg = `Enrollment error: ${enrollErr.message}`;
            }
            try {
              await deleteCandidate(newId);
            } catch (delErr) {
              console.error("Rollback delete failed:", delErr);
            }
            toast.error(userMsg, { id: "save" });
            setSaving(false);
            return;
          }
        }
        if (form.aadharFace) localStorage.setItem(`dbskills_aadhar_face_${newId}`, form.aadharFace);
        if (form.dlFace) localStorage.setItem(`dbskills_dl_face_${newId}`, form.dlFace);

        // LOG ACTIONS: REGISTRATION_COMPLETED and CANDIDATE_CREATED
        if (registrationStartedAt) {
          const completedAtTime = new Date().toISOString();
          const duration = Date.now() - new Date(registrationStartedAt).getTime();
          safeLogClientAction("REGISTRATION_COMPLETED", "Candidate", {
            startedAt: registrationStartedAt,
            completedAt: completedAtTime,
            durationMs: duration,
            candidateId: newId,
            success: true
          });
        }
        safeLogClientAction("CANDIDATE_CREATED", "Candidate", {
          candidateId: newId,
          success: true
        });
        setRegistrationStartedAt(null);
      }
    }

    toast.success("Saved successfully", { id: "save" });
    setCapturedFaceImage(null);
    setCapturedFaceDescriptor(null);
    await fetchCandidates();
  } catch (err) {
    console.error(err);
    toast.error("Save failed", { id: "save" });
  } finally {
    setSaving(false);
  }
};






    const askDelete = (id) => {
      setDeleteId(id);
      setDeleteDialogOpen(true);
    };

    const performDelete = async () => {
      try {
        await deleteCandidate(deleteId);
        setDeleteDialogOpen(false);
        fetchCandidates();
        alert("Deleted");
      } catch (error) {
        console.error("Delete error:", error);
      }
    };
const handleProjectChange = async (e) => {
  const project = e.target.value;

  setForm((prev) => ({
    ...prev,
    project,
    program: "",
  }));

  if (project) {
    if (role !== "admin" && role !== "superAdmin") {
      try {
        const res = await getMyBranch();
        const branch = res.data;
        if (Array.isArray(branch.project)) {
          const assignedProgs = branch.project
            .filter(item => (item.project || item.projectName) === project)
            .map(item => item.program || item.programName)
            .filter(Boolean);
          setPrograms(assignedProgs);
          return;
        }
      } catch (err) {
        console.error("Failed to load branch programs", err);
      }
    }
    const res = await getProgramsByProject(project);
    setPrograms(res.data);
  } else {
    setPrograms([]);
  }
};


    // View popup component
    const DetailItem = ({ label, value }) => (
      <div>
        <p className="text-[11.5px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="font-semibold text-slate-800 text-[13.5px] mt-0.5">{value || "—"}</p>
      </div>
    );
  const fieldLabels = {
    project: "Project",
    program: "Program",
    location: "Location",
    // status: "Status",
    batchId: "Batch ID",
    name: "Candidate Name",
    fathersName: "Father's Name",
    mothersName: "Mother's Name",
    maritalStatus: "Marital Status",
    aadharNumber: "Aadhar Number",
    dob: "Date of Birth",
    gender: "Gender",
    caste: "Caste",
    address: "Address",

    religion: "Religion",
    vulnerability: "Vulnerability",
    annualIncome: "Annual Income",
    qualification: "Qualification",
    contactNumber: "Contact Number",
    assessmentDate: "Assessment Date",
      dlIssueDate: "DL Issue Date",
    dlAuthority: "DL Issuing Authority",
    mobileNumber: "Mobile Number",
    experienceYears: "Experience (Years)",
    employerName: "Present Employer Name",
    employerAddress: "Employer Address & Contact No",
    dlNo: "DL Number",
    dlType: "DL Type",
    licenseExpiryDate: "License Expiry Date",
    dependentFamilyMembers: "Dependent Family Members",
    ownerOrDriver: "Owner / Driver",
    abha: "ABHA Number",
    result: "Result",
    certificateNo: "Certificate Number",
    remarks: "Remarks",
    ekycRemarks: "eKYC Remarks",
    ekycRegisteredEmail: "eKYC Registered Email",
    barCode: "Bar Code",
    aadharFile: "Aadhaar Front Upload",
    aadharBackFile: "Aadhaar Back Upload",
    dlFile: "Driving Licence Front Upload",
    dlBackFile: "Driving Licence Back Upload",
    otherFile: "Other Documents",

  
  };



// ⭐ Resets the whole scan dialog back to step 1
const resetScanFlow = () => {
  setScanStep("aadhar");
  setScanImage(null);
  setDlScanImage(null);
  setExtractedAadharData(null);
  setExtractedDlData(null);
  setAadharRawText("");
  setDlRawText("");
  stopCamera();
};

const handleStartScanRegistration = () => {
  setCreateCandidateDialogOpen(false);
  navigate("/scan-aadhar-dl");
};

const handleStartManualRegistration = () => {
  setCreateCandidateDialogOpen(false);
  openCreate();
};

// ⭐ Step 1 button: extract Aadhaar, then move to the DL step
const handleExtractAadhaar = async () => {
  if (!scanImage) {
    toast.error("Please upload or capture the Aadhaar image");
    return;
  }
  try {
    setExtracting(true);
    toast.loading("Extracting Aadhaar details...", { id: "aadhaar-extract" });
    const response = await extractAadhaarFromImage(scanImage);
    const fields = response.data;
    const rawText = response.rawText || "";

    if (!fields.aadharNumber) {
      toast.dismiss("aadhaar-extract");
      toast.error("Aadhaar number not detected. Please try a clearer image.");
      return;
    }

    setExtractedAadharData(fields);
    setAadharRawText(rawText);
    toast.success("Aadhaar details extracted", { id: "aadhaar-extract" });
    setScanStep("dl");
  } catch (error) {
    console.error("Aadhaar extraction failed:", error);
    toast.error("Aadhaar extraction failed", { id: "aadhaar-extract" });
  } finally {
    setExtracting(false);
  }
};

// ⭐ Step 2 button: extract DL, then move to the review step
const handleExtractDl = async () => {
  if (!dlScanImage) {
    toast.error("Please upload or capture the Driving Licence image");
    return;
  }
  try {
    setExtracting(true);
    toast.loading("Extracting Driving Licence details...", { id: "dl-extract" });
    const response = await extractDlFromImage(dlScanImage);
    const fields = response.data;
    const rawText = response.rawText || "";

    setExtractedDlData(fields);
    setDlRawText(rawText);
    toast.success("Driving Licence details extracted", { id: "dl-extract" });
    setScanStep("review");
  } catch (error) {
    console.error("DL extraction failed:", error);
    toast.error("Driving Licence extraction failed", { id: "dl-extract" });
  } finally {
    setExtracting(false);
  }
};

// ⭐ Lets the user skip the DL scan and still review/continue with Aadhaar only
const handleSkipDl = () => {
  setExtractedDlData(null);
  setDlRawText("");
  setScanStep("review");
};

// ⭐ Step 3 button: verify Aadhaar against backend, then hand data to the candidate form
const handleContinueToCandidate = async () => {
  if (!extractedAadharData?.aadharNumber) {
    toast.error("Aadhaar number missing — please redo the Aadhaar scan");
    return;
  }

  try {
    setExtracting(true);
    const res = await checkAadhar(extractedAadharData.aadharNumber);

    if (res.data.exists) {
      toast.error("Candidate already registered");
      setSelectedCandidate(res.data.candidate);
      setViewDialogOpen(true);
      setScanDialog(false);
      resetScanFlow();
      return;
    }

    const aadharFile = blobToFile(scanImage, "aadhar-scan.jpg");
    const dlFile = blobToFile(dlScanImage, "dl-scan.jpg");

    setOcrCandidateData({
      ...extractedAadharData,
      ...(extractedDlData || {}),
      aadharFile,
      dlFile,
    });

    setScanDialog(false);
    setConfirmCreateDialog(true);
    resetScanFlow();
  } catch (error) {
    console.error("Aadhaar verification failed:", error);
    toast.error("Could not verify Aadhaar, please try again");
  } finally {
    setExtracting(false);
  }
};


const startCamera = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" }, // back camera
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });

    setCameraStream(stream);
    setCameraOpen(true);

    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    }, 200); // small delay ensures videoRef exists
  } catch (err) {
    console.error(err);
    toast.error("Camera access denied");
  }
};
const captureImage = () => {
  const video = videoRef.current;
  const canvas = canvasRef.current;

  const scale = 2; // higher quality

  canvas.width = video.videoWidth * scale;
  canvas.height = video.videoHeight * scale;

  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);
  ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

  canvas.toBlob((blob) => {
    const file = new File(
      [blob],
      scanStep === "dl" ? "dl.jpg" : "aadhaar.jpg",
      { type: "image/jpeg" }
    );
    if (scanStep === "dl") {
      setDlScanImage(file);
    } else {
      setScanImage(file);
    }
    stopCamera();
  }, "image/jpeg", 1);
};
const stopCamera = () => {
  cameraStream?.getTracks().forEach((track) => track.stop());
  setCameraStream(null);
  setCameraOpen(false);
};
const startFaceCamera = async () => {
  if (faceCameraStream) stopFaceCamera();
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user" },
      audio: false
    });
    setFaceCameraStream(stream);
    setFaceCameraOpen(true);
    setTimeout(() => {
      const v = document.getElementById("face-video-feed");
      if (v) {
        v.srcObject = stream;
        v.play();
      }
    }, 500);
  } catch (err) {
    console.error("Face camera access error:", err);
    toast.error("Could not access front camera");
  }
};
const loadFaceApiModels = async () => {
  if (!window.faceapi) return false;
  const nets = window.faceapi.nets;
  if (!nets.ssdMobilenetv1.isLoaded || !nets.faceLandmark68Net.isLoaded || !nets.faceRecognitionNet.isLoaded) {
    console.log("Loading FaceAPI models in Candidates...");
    await nets.ssdMobilenetv1.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
    await nets.faceLandmark68Net.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
    await nets.faceRecognitionNet.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
    console.log("FaceAPI models loaded successfully");
  }
  return true;
};

const getFaceDescriptor = async (imageSrc) => {
  if (!window.faceapi) return null;
  try {
    await loadFaceApiModels();
  } catch (err) {
    console.error("Error loading models in Candidates:", err);
    return null;
  }
  
  const img = new Image();
  img.src = imageSrc;
  await new Promise((resolve) => {
    img.onload = resolve;
  });
  try {
    const detection = await window.faceapi
      .detectSingleFace(img, new window.faceapi.SsdMobilenetv1Options({ minConfidence: 0.65 }))
      .withFaceLandmarks()
      .withFaceDescriptor();
    return detection ? detection.descriptor : null;
  } catch (e) {
    console.error("FaceAPI extraction error:", e);
    return null;
  }
};

const cropFaceFromImageFile = async (imageFileOrUrl) => {
  if (!window.faceapi || !imageFileOrUrl) return null;
  try {
    await loadFaceApiModels();
    let img;
    if (typeof imageFileOrUrl === "string") {
      img = await window.faceapi.fetchImage(imageFileOrUrl);
    } else {
      const url = URL.createObjectURL(imageFileOrUrl);
      img = await window.faceapi.fetchImage(url);
    }

    const detection = await window.faceapi.detectSingleFace(
      img,
      new window.faceapi.SsdMobilenetv1Options({ minConfidence: 0.3 })
    );

    if (!detection) return null;

    const { x, y, width, height } = detection.box;
    const marginX = width * 0.25;
    const marginY = height * 0.3;
    const cropX = Math.max(0, x - marginX);
    const cropY = Math.max(0, y - marginY);
    const cropWidth = Math.min(img.width - cropX, width + marginX * 2);
    const cropHeight = Math.min(img.height - cropY, height + marginY * 2);

    const canvas = document.createElement("canvas");
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    return canvas.toDataURL("image/jpeg", 0.9);
  } catch (err) {
    console.error("Face cropping failed:", err);
    return null;
  }
};

const compareFacesAndCalculateScore = async (face1Src, face2Src) => {
  if (!window.faceapi || !face1Src || !face2Src) return null;
  try {
    const desc1 = await getFaceDescriptor(face1Src);
    const desc2 = await getFaceDescriptor(face2Src);

    if (!desc1 || !desc2) return null;

    const distance = window.faceapi.euclideanDistance(desc1, desc2);
    let matchPercentage = Math.max(0, Math.min(100, (1 - (distance / 0.7)) * 100));
    if (distance < 0.35) {
      matchPercentage = 92 + ((0.35 - distance) / 0.35) * 8;
    } else if (distance < 0.55) {
      matchPercentage = 80 + ((0.55 - distance) / 0.2) * 12;
    }

    const score = parseFloat(matchPercentage.toFixed(2));
    const isVerified = score >= 80;
    const confidence = score >= 95 ? "High" : score >= 80 ? "Medium" : "Low";

    return {
      score,
      verified: isVerified,
      confidence,
      verifiedAt: new Date().toISOString()
    };
  } catch (err) {
    console.error("Face comparison failed:", err);
    return null;
  }
};



const captureFaceImage = () => {
  safeLogClientAction("BIOMETRIC_STARTED", "Biometric");
  const video = document.getElementById("face-video-feed");
  const canvas = document.createElement("canvas");
  if (!video) return;

  canvas.width = video.videoWidth || 640;
  canvas.height = video.videoHeight || 480;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const base64 = canvas.toDataURL("image/jpeg");

  setFaceScanning(true);
  setFaceScanProgress(0);

  let progress = 0;
  const interval = setInterval(async () => {
    progress += 20;
    setFaceScanProgress(progress);
    if (progress >= 100) {
      clearInterval(interval);
      
      const checkToastId = toast.loading("Analyzing captured face...", { id: "capture-check" });
      try {
        // 1. Detect if a face is actually present
        const descriptor = await getFaceDescriptor(base64);
        if (!descriptor) {
          toast.error("No face detected in live capture! Please capture again with a clear face visible.", { id: "capture-check" });
          setFaceScanning(false);
          return;
        }

        // 2. Perform live matching if Aadhaar or DL face is available
        const docFace = form.aadharFace || form.dlFace;
        let matchRes = null;
        if (docFace) {
          matchRes = await compareFacesAndCalculateScore(base64, docFace);
          if (matchRes) {
            setLiveVerificationResult(matchRes);
            if (!matchRes.verified) {
              toast.error(`Unknown Face Warning! Captured face does not match document photo (Match: ${matchRes.score}%)`, { id: "capture-check", duration: 5000 });
            } else {
              toast.success(`Face Verified successfully! (Match: ${matchRes.score}%)`, { id: "capture-check" });
            }
          } else {
            toast.dismiss("capture-check");
          }
        } else {
          toast.success("Face captured successfully.", { id: "capture-check" });
        }

        setCapturedFaceImage(base64);

        if (editingId) {
          toast.loading("Enrolling biometric face template...", { id: "enroll" });
          try {
            await enrollFaceOnBackend(editingId, base64);
            toast.success("Biometric face updated successfully.", { id: "enroll" });
            setForm((prev) => ({
              ...prev,
              biometricFace: base64,
              biometricFacePy: { enrolled: true },
              ...(matchRes ? {
                faceMatchScore: matchRes.score,
                faceVerified: matchRes.verified,
                confidence: matchRes.confidence,
                verifiedAt: matchRes.verifiedAt
              } : {})
            }));
            stopFaceCamera();
            safeLogClientAction("BIOMETRIC_COMPLETED", "Biometric", { success: true, candidateId: editingId, mode: "Re-enrollment" });
            fetchCandidates();
          } catch (err) {
            console.error("Biometric enrollment error:", err);
            let userMsg = "Biometric enrollment failed. Please try again.";
            if (err.message.includes("No face detected")) {
              userMsg = "No face detected. Please position the candidate correctly.";
            } else if (err.message.includes("Multiple faces")) {
              userMsg = "Multiple faces detected. Please make sure only the candidate is visible.";
            } else if (err.message.includes("Failed to connect") || err.message.includes("Fetch")) {
              userMsg = "Unable to connect to biometric service.";
            } else {
              userMsg = `Enrollment error: ${err.message}`;
            }
            toast.error(userMsg, { id: "enroll" });
            safeLogClientAction("BIOMETRIC_FAILED", "Biometric", { success: false, error: err.message, candidateId: editingId, mode: "Re-enrollment" });
            stopFaceCamera();
          }
        } else {
          setForm((prev) => ({
            ...prev,
            biometricFace: base64,
            ...(matchRes ? {
              faceMatchScore: matchRes.score,
              faceVerified: matchRes.verified,
              confidence: matchRes.confidence,
              verifiedAt: matchRes.verifiedAt
            } : {})
          }));
          stopFaceCamera();
        }
      } catch (err) {
        console.error("Verification failed during capture:", err);
        toast.error("Analysis failed. Please try capturing again.", { id: "capture-check" });
      } finally {
        setFaceScanning(false);
      }
    }
  }, 100);
};
const stopFaceCamera = () => {
  if (faceCameraStream) {
    faceCameraStream.getTracks().forEach((track) => track.stop());
    setFaceCameraStream(null);
  }
  setFaceCameraOpen(false);
};
const nonRequiredFields = [
  // "status",
  "batchId",
  "certificateNo",
  "barCode",
  "result",
  "ekycRegisteredEmail",
  "abha",
  "remarks",
  "employerAddress",
  "ekycRemarks",
  "vulnerability",
  "aadharFace",
  "dlFace",
  "faceMatchScore",
  "faceVerified",
  "confidence",
  "verifiedAt",
  "biometricFace",
  "biometricFaceDesc",
  "attendanceLogs"
];
const adminOnlyFields = [
  "result",
  "certificateNo",
  "barCode",
  "batchId",
];

const validateForm = () => {
  /* ================= RELIGION ================= */
  if (form.religion === "Others" && !form.religionOther?.trim()) {
    toast.error("Please enter religion");
    return false;
  }

  /* ================= REQUIRED FILES (CREATE ONLY) ================= */
if (!editingId) {
  if (!form.aadharFile) {
    toast.error("Please upload Aadhaar Front document");
    return false;
  }

  if (!form.dlFile) {
    toast.error("Please upload Driving License Front document");
    return false;
  }
}


  /* ================= GENERAL REQUIRED FIELDS ================= */
  for (const key of Object.keys(emptyCandidate)) {
    // 🔒 Skip admin-only fields for non-admin users
    if (adminOnlyFields.includes(key) && role !== "admin") continue;

    // Skip optional + non-required fields
    if (optionalFields.includes(key)) continue;
    if (nonRequiredFields.includes(key)) continue;

    // Skip conditional / already validated fields
    if (key === "religionOther") continue;
    if (key === "aadharFile" || key === "aadharBackFile" || key === "dlFile" || key === "dlBackFile") continue;

    const value = form[key];

    if (
      value === undefined ||
      value === null ||
      value.toString().trim() === ""
    ) {
      toast.error(`Please fill ${fieldLabels[key] || key}`);
      return false;
    }
  }

  return true;
};

/* ── completion helper (lifted to component scope for progress ring) ── */
const pct = (fields) => {
  const applicable = fields.filter(f => {
    if (adminOnlyFields.includes(f) && role !== "admin") return false;
    if (["religionOther"].includes(f)) return false;
    return true;
  });
  if (!applicable.length) return { filled: 0, total: 0 };
  const filled = applicable.filter(f => {
    const v = form[f];
    return v !== undefined && v !== null && String(v).trim() !== "";
  }).length;
  return { filled, total: applicable.length };
};

const getRequiredFields = () => {
  const req = Object.keys(emptyCandidate).filter(key => {
    if (adminOnlyFields.includes(key) && role !== "admin") return false;
    if (optionalFields.includes(key)) return false;
    if (nonRequiredFields.includes(key)) return false;
    if (key === "religionOther") return false;
    if (key === "aadharFile" || key === "aadharBackFile" || key === "dlFile" || key === "dlBackFile") return false;
    return true;
  });
  if (!editingId) {
    req.push("aadharFile");
    req.push("dlFile");
  }
  return req;
};


const handleAadharChange = async (e) => {
  const value = e.target.value.replace(/\D/g, "");

  setForm((prev) => ({ ...prev, aadharNumber: value }));

  if (value.length < 12) {
    setAadharStatus("invalid");
    return;
  }

  if (value.length > 12) return;

  try {
    setAadharStatus("checking");
    const res = await checkAadhar(value);

    if (res.data.exists) {
      setAadharStatus("exists");
    } else {
      setAadharStatus("available");
    }
  } catch (err) {
    console.error(err);
    setAadharStatus(null);
  }
};
const handleAbhaChange = (e) => {
  const value = e.target.value.replace(/\D/g, "");

  setForm((prev) => ({ ...prev, abha: value }));

  if (value.length === 0) {
    setAbhaError("");
  } else if (value.length !== 14) {
    setAbhaError("ABHA must be 14 digits");
  } else {
    setAbhaError("");
  }
};
const handleContactChange = (e) => {
  const value = e.target.value.replace(/\D/g, "");

  setForm((prev) => ({ ...prev, contactNumber: value }));

  if (value.length === 0) {
    setContactError("");
  } else if (value.length !== 10) {
    setContactError("Contact number must be 10 digits");
  } else {
    setContactError("");
  }
};


const FILE_BASE_URL = "https://api2.eduforestin.com/"
// const FILE_BASE_URL = "http://localhost:4000"

  

const isOneDayCourse = (candidate) => {
  if (!candidate.program) return false;
  const prog = candidate.program.toLowerCase();
  if (prog.includes("1 day") || prog.includes("one day")) return true;
  
  if (rawProjectsList.length > 0 && candidate.project) {
    const matched = rawProjectsList.find(
      (p) => 
        p.projectName.trim().toLowerCase() === candidate.project.trim().toLowerCase() &&
        p.programName.trim().toLowerCase() === candidate.program.trim().toLowerCase()
    );
    if (matched && matched.courseDuration) {
      const durationText = matched.courseDuration.toLowerCase();
      if (durationText.includes("1 day") || durationText.includes("one day")) {
        return true;
      }
    }
  }
  return false;
};

const getFileUrl = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return "";
  if (value.startsWith("data:") || value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  const cleanPath = value.replace(/^\/+/, "");
  return `${FILE_BASE_URL}/${cleanPath}`;
};

    return (
      <Box sx={{  minHeight: "100vh" }} p={{ xs: 2, sm: 3, md: 4 }}>

        {/* ================= PAGE HEADER ================= */}
        <Box mb={3}>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
            Candidates
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: "#64748b", mt: 0.3, fontWeight: 500 }}>
            Manage enrollment, verification and attendance for every candidate.
          </Typography>
        </Box>

{/* ================= TOP SEARCH ================= */}
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
    flexWrap: "wrap",
  }}
>

 
 <div className="relative w-full max-w-xl">
  <div className="relative flex items-center h-[48px] rounded-xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition focus-within:ring-2 focus-within:ring-indigo-500/25 focus-within:border-indigo-400">

    <span className="pl-4 text-slate-400">
      <MagnifyingGlassIcon className="w-[18px] h-[18px]" />
    </span>

    <input
      type="text"
      value={search}
      onChange={(e) => {
        setSearch(e.target.value);
        setPage(0);
      }}
      placeholder="Search by name, Aadhaar or mobile"
      className="
        flex-1 h-full bg-transparent px-3
        text-[13.5px] text-slate-700 placeholder-slate-400
        focus:outline-none
      "
    />

    {search && (
      <>
        <div className="h-5 w-px bg-slate-200 mx-1"></div>
        <button
          onClick={() => {
            setSearch("");
            setPage(0);
          }}
          className="mr-3.5 w-7 h-7 rounded-full flex items-center justify-center
                     text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </>
    )}
  </div>
</div>
<div className="relative w-full sm:w-60">
  <div className="relative flex items-center h-[48px] rounded-xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition focus-within:ring-2 focus-within:ring-indigo-500/25 focus-within:border-indigo-400">

    {/* 📍 Icon */}
    <span className="pl-4 text-slate-400">
      <i className="ri-map-pin-line text-[15px]"></i>
    </span>

   {role === "branchUser" ? (
  <input
    value={selectedLocation}
    disabled
    className="
      flex-1 h-full bg-transparent
      pl-3 pr-4
      text-[13.5px] text-slate-600
      cursor-not-allowed
    "
  />
) : (
  <select
    value={selectedLocation}
    onChange={(e) => {
      setSelectedLocation(e.target.value);
      setPage(0);
    }}
    className="
      flex-1 h-full bg-transparent
      pl-3 pr-10
      text-[13.5px] text-slate-700
      appearance-none
      focus:outline-none
      cursor-pointer
    "
  >
    <option value="">All Locations</option>
    {locations.map((loc) => (
      <option key={loc} value={loc}>
        {loc}
      </option>
    ))}
  </select>
)}


    {/* ⬇ Arrow (ADMIN ONLY) */}
    {role !== "branchUser" && (
      <span className="absolute right-4 text-slate-400 pointer-events-none">
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </span>
    )}
  </div>
</div>

{/* 📊 EXPORT BUTTONS */}


</Box>



    
<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
  
<Box
  sx={{
    width: "100%",
    mb: 3,
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    alignItems: "center",
    justifyContent: "space-between",

    "@media (max-width: 640px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  }}
>
  
  

  
  

<Box
  className="
    w-full
    flex items-center justify-between
    gap-4
    overflow-x-auto
    pb-2
    scrollbar-hide
  "
>
  {/* LEFT ACTIONS */}
  <div className="flex gap-3 shrink-0">
    {/* CHECK AADHAAR */}
    <button
      onClick={() => {
    safeLogClientAction("CHECK_AADHAAR", "Candidate", {
      source: "button",
    });

    setCheckAadharValue("");
    setAadharCheckStatus("");
    setAadharDialogOpen(true);
  }}
      className={ui.pillBtnDark}
    >
      <IdentificationIcon className="w-[18px] h-[18px]" />
      Check Aadhaar
    </button>

    {/* SCAN AADHAAR & DL */}
  <button
  onClick={() => {
    safeLogClientAction("SCAN_AADHAAR_DL", "Candidate", {
      source: "button",
    });
    navigate("/scan-aadhar-dl");
  }}
 

     
      className={ui.pillBtnPrimary}
    >
      <CameraIcon className="w-[18px] h-[18px]" />
      Scan Aadhaar &amp; DL
    </button>
  </div>

  {/* RIGHT ACTIONS */}
  <div className="flex gap-3 shrink-0 ml-auto">
    {/* EXPORT */}
    {/* <button
      onClick={() => setExportDialog(true)}
      className="
        flex items-center gap-2
        h-[44px]
        px-5
        rounded-xl
        border border-gray-300
        bg-white
        text-gray-700
        font-medium
        shadow-sm
        hover:bg-gray-50
        transition
        whitespace-nowrap
      "
    >
      <DocumentArrowDownIcon className="w-5 h-5" />
      Export Excel
    </button> */}
 
<div className="flex items-center gap-2.5">
  <button
    type="button"
    onClick={() => {
      setExportType("excel");
      setExportDialogOpen(true);
    }}
    className="flex items-center gap-2 h-[48px] px-4 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-[13.5px] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-50 transition active:scale-[0.98] whitespace-nowrap cursor-pointer"
  >
    <DocumentArrowDownIcon className="w-5 h-5 text-emerald-600" />
    <span>Export Excel</span>
  </button>

  <button
    type="button"
    onClick={() => {
      setExportType("pdf");
      setExportDialogOpen(true);
    }}
    className="flex items-center gap-2 h-[48px] px-4 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-[13.5px] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-50 transition active:scale-[0.98] whitespace-nowrap cursor-pointer"
  >
    <DocumentArrowDownIcon className="w-5 h-5 text-indigo-600" />
    <span>Export PDF</span>
  </button>
</div>
{role === "superAdmin" && (
  <button
    onClick={() => {
      safeLogClientAction('CSV_UPLOAD_CLICK', 'Candidate', { source: 'button' });
      setCsvDialogOpen(true);
    }}
    className={ui.pillBtnPurple}
  >
    <DocumentArrowDownIcon className="w-[18px] h-[18px]" />
    Upload CSV
  </button>
)} 
    {/* CREATE */}
    <button
      onClick={() => {
        setCreateCandidateDialogOpen(true);
      }}
      className={ui.pillBtnSuccess}
    >
      <PlusIcon className="w-[18px] h-[18px]" />
      Create Candidate
    </button>
  </div>
</Box>



</Box>


  </Box>





    <TableContainer
      component={Paper}
      sx={{
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
        overflowX: "auto",
      }}
    >
      <Table sx={{ minWidth: 900 }}>
        {/* ================= TABLE HEAD ================= */}
        <TableHead>
          <TableRow
            sx={{
              bgcolor: "#f8fafc",
              "& th": {
                fontSize: "11.5px",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                borderBottom: "1px solid #e2e8f0",
              },
            }}
          >
            <TableCell>#</TableCell>
            <TableCell>Candidate</TableCell>

            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
              Location
            </TableCell>

            <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
              Aadhaar No
            </TableCell>

            <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
              Contact
            </TableCell>

            <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
              Created Date
            </TableCell>
<TableCell>Status</TableCell>
            <TableCell>1-Day Attendance</TableCell>

            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>

        {/* ================= TABLE BODY ================= */}
        <TableBody
          sx={{
            "& td": {
              fontSize: "13px",
              color: "#1e293b",
              borderBottom: "1px solid #f1f5f9",
            },
            "& tr:hover": { backgroundColor: "#f8fafc" },
          }}
        >
          {loadingTable ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#64748b", fontWeight: 600 }}>
                Loading...
              </TableCell>
            </TableRow>
          ) : data.length ? (
            data.map((c, index) => (
              <TableRow key={c._id} hover>
                {/* Index */}
                <TableCell>
                  <strong className="text-slate-400">{page * rowsPerPage + index + 1}</strong>
                </TableCell>

                {/* Candidate */}
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <div className="w-10 h-10 rounded-full border border-indigo-200 overflow-hidden bg-indigo-50 flex-shrink-0 shadow-sm flex items-center justify-center">
                      {c.biometricFace ? (
                        <img
                          src={c.biometricFace}
                          alt="avatar"
                          className="w-full h-full object-cover transform -scale-x-100"
                        />
                      ) : (
                        <span className="text-indigo-600 font-bold text-[13px]">
                          {c.name?.charAt(0)?.toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div>
                      <p className="font-semibold text-slate-800 text-[13.5px]">{c.name?.toUpperCase()}</p>

                      {/* Mobile-only info */}
                      <p className="text-[11.5px] text-slate-400 font-medium sm:hidden">
                        {c.location}
                      </p>
                    </div>
                  </Box>
                </TableCell>

                {/* Location */}
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  {c.location}
                </TableCell>

                {/* Aadhaar */}
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                  {c.aadharNumber}
                </TableCell>

                {/* Contact */}
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                  {c.contactNumber}
                </TableCell>

                {/* Created Date */}
                <TableCell sx={{ display: { xs: "none", lg: "table-cell" }, color: "#64748b" }}>
                  {c.createdAt
                    ? new Date(c.createdAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "—"}
                </TableCell>
<TableCell>
  <span
    className={`px-2.5 py-1 rounded-full text-[10.5px] font-bold tracking-wide
      ${
        c.approvalstatus === "Approved"
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : c.approvalstatus === "Rejected"
          ? "bg-red-50 text-red-700 border border-red-200"
          : "bg-amber-50 text-amber-700 border border-amber-200"
      }
    `}
  >
    {c.approvalstatus}
  </span>

  {c.approvalstatus === "Rejected" && c.approvalReason && (
    <p className="text-[11px] text-red-500 font-medium mt-1">
      Reason: {c.approvalReason}
    </p>
  )}
</TableCell>

                <TableCell>
                  {isOneDayCourse(c) ? (
                    (() => {
                      const serverAttendance = c.attendanceLogs || [];
                      const attendanceKey = `dbskills_attendance_${c._id}`;
                      const localAttendance = JSON.parse(localStorage.getItem(attendanceKey) || "[]");
                      
                      const combined = [...serverAttendance];
                      localAttendance.forEach(item => {
                        const dayNum = typeof item === 'number' ? item : item.day;
                        if (!combined.some(r => r.day === dayNum)) {
                          combined.push(typeof item === 'number' ? { day: item, verifiedAt: null } : item);
                        }
                      });

                      const record = combined.find(r => r?.day === 1);

                      if (record) {
                        return (
                          <div className="flex flex-col items-start gap-0.5">
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              <FaCheckCircle className="text-emerald-500 text-xs" /> Verified
                            </span>
                            {record.verifiedAt && (
                              <span className="text-[9px] text-slate-400 font-semibold select-all leading-tight">
                                {new Date(record.verifiedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                <br />
                                {new Date(record.verifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        );
                      } else {
                        return (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                            Pending Scan
                          </span>
                        );
                      }
                    })()
                  ) : (
                    <span className="text-[11px] text-slate-300 font-medium">—</span>
                  )}
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <Box display="flex" gap={1}>
{(role === "admin" || role === "superAdmin") && c.approvalstatus === "Pending" && (
  <button
    onClick={() => {
      setApprovalCandidate(c);
      setApprovalStatus("");
      setApprovalReason("");
      setApprovalDialog(true);
    }}
    className="p-2 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition"
    title="Approve / Reject"
  >
    <CheckCircleIcon className="w-[18px] h-[18px] text-emerald-600" />
  </button>
)}

                    <button
                      onClick={() => {
                        setSelectedCandidate(c);
                        setViewDialogOpen(true);
                      }}
                      className={ui.iconBtn}
                      title="View"
                    >
                      <BiShow size={17} className="text-indigo-600" />
                    </button>

                 <button
  type="button"   // 🔴 IMPORTANT
  onClick={() => openEdit(c)}
  className={ui.iconBtn}
  title="Edit"
>
  <BiEdit size={17} className="text-amber-600" />
</button>


                    <button
                      onClick={() => askDelete(c._id)}
                      className={ui.iconBtn}
                      title="Delete"
                    >
                      <BiTrash size={17} className="text-red-600" />
                    </button>

                    {!isOneDayCourse(c) && (
                      <button
                        title="Attendance Log"
                        onClick={() => {
                          const serverAttendance = c.attendanceLogs || [];
                          const attendanceKey = `dbskills_attendance_${c._id}`;
                          const localAttendance = JSON.parse(localStorage.getItem(attendanceKey) || "[]");
                          
                          const combined = [...serverAttendance];
                          localAttendance.forEach(item => {
                            const dayNum = typeof item === 'number' ? item : item.day;
                            if (!combined.some(r => r.day === dayNum)) {
                              combined.push(typeof item === 'number' ? { day: item, verifiedAt: null, photo: null } : item);
                            }
                          });

                          const formattedRecords = combined.map(item => {
                            if (typeof item === 'number') {
                              return { day: item, verifiedAt: null, photo: null };
                            }
                            return item;
                          });
                          setSelectedLogCandidate(c);
                          setSelectedLogRecords(formattedRecords);
                          setAttendanceLogOpen(true);
                        }}
                        className="p-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition"
                      >
                        <FaCalendarCheck size={17} className="text-indigo-600" />
                      </button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#94a3b8", fontWeight: 600 }}>
                No candidates found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* ================= PAGINATION ================= */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{
          borderTop: "1px solid #e2e8f0",
          "& .MuiTablePagination-toolbar": { fontSize: "13px" },
        }}
      />
    </TableContainer>

  {/* DRAWER */}
  <Drawer
    anchor="right"
    open={openDrawer}
    onClose={closeDrawer}
    PaperProps={{
      sx: {
        width: { xs: "100%", sm: "85%", md: "65%", lg: "55%", xl: "60%" },
        backgroundColor: "#f8fafc",
        p: 0,
        display: "flex",
        flexDirection: "column",
      },
    }}
  >
    {/* ── Sticky header with progress ring ── */}
    {(() => {
      /* Gather all required fields for overall progress */
      const reqFields = getRequiredFields();
      const emptyReqFields = reqFields.filter(f => {
        const v = form[f];
        return v === undefined || v === null || String(v).trim() === "";
      });
      const totalCount = reqFields.length;
      const totalFilled = totalCount - emptyReqFields.length;
      const overallPct = totalCount > 0 ? Math.round((totalFilled / totalCount) * 100) : 0;
      const ringRadius = 18;
      const ringCircumference = 2 * Math.PI * ringRadius;
      const ringOffset = ringCircumference - (overallPct / 100) * ringCircumference;
      const ringColor = overallPct >= 100 ? "#10B981" : overallPct >= 50 ? "#2BB5CE" : "#F59E0B";
      const fieldsRemaining = emptyReqFields.length;

      return (
        <Box
          sx={{
            px: 4,
            pt: 3,
            pb: 2.5,
            borderBottom: "1px solid #e2e8f0",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          <div className="flex items-center gap-4">
            {/* Progress Ring */}
            <div className="flex flex-col items-center flex-shrink-0">
              <svg width="44" height="44" viewBox="0 0 44 44">
                <circle className="progress-ring-circle-bg" cx="22" cy="22" r={ringRadius} strokeWidth="3" />
                <circle
                  className="progress-ring-circle"
                  cx="22" cy="22" r={ringRadius}
                  strokeWidth="3"
                  stroke={ringColor}
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringOffset}
                />
                <text x="22" y="22" textAnchor="middle" dominantBaseline="central"
                  style={{ fontSize: "11px", fontWeight: 800, fill: ringColor }}
                >
                  {overallPct}
                </text>
              </svg>
              <span className="text-[9.5px] font-bold mt-0.5 tracking-tight" style={{ color: ringColor }}>
                {overallPct}% complete
              </span>
            </div>
            {/* Title */}
            <div className="flex-1 min-w-0">
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                {editingId ? "Edit Candidate" : "Create Candidate"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.3, fontWeight: 500 }}>
                {editingId
                  ? "Update candidate details, documents and verification status."
                  : "Fill in candidate details and upload the required documents."}
              </Typography>
            </div>
          </div>
          {/* expose fieldsRemaining for footer */}
          <input type="hidden" id="__drawer_fields_remaining" value={fieldsRemaining} />
        </Box>
      );
    })()}

    {/* ── Body: rail + scrollable content ── */}
    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

      {/* ── Section-jump rail (desktop only) ── */}
      <div className="drawer-section-rail">
        {SECTION_META.filter(s => s.id !== "sec-admin" || role === "admin").map((sec) => (
          <div
            key={sec.id}
            className={`rail-dot ${activeSection === sec.id ? "active" : ""}`}
            style={{ color: sec.color, "--dot-ring": sec.ring }}
            onClick={() => {
              const el = scrollBodyRef.current?.querySelector(`#${sec.id}`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            <span className="rail-tooltip">{sec.label}</span>
          </div>
        ))}
      </div>

      {/* ── Scrollable body ── */}
      <Box ref={scrollBodyRef} sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 4 }, py: 3 }}>

      {/* === Project / Program pill selectors (unchanged) === */}
      {role === "branchUser" && projects.length <= 1 ? (
        <div className={`${ui.card} p-4 mb-5 flex flex-col gap-1.5`}>
          <div className={ui.sectionTitle}>Assigned Program</div>
          <div className="text-sm font-bold text-slate-800">
            {form.project || "No Project Assigned"}{" "}
            <span className="text-xs text-slate-500 font-medium">({form.program || "No Program Assigned"})</span>
          </div>
        </div>
      ) : (
        <div className={`${ui.card} p-5 space-y-4 mb-5`}>
          <div className="flex flex-col">
            <label className={ui.label}>Project</label>
            <div className="flex flex-wrap gap-2.5">
              {projects.map((p) => {
                const active = form.project === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleProjectChange({ target: { value: p } })}
                    className={`px-4 py-2 rounded-full border text-[13px] font-semibold transition ${
                      active
                        ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                        : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
          {form.project && (
            <div className="flex flex-col mt-4">
              <label className={ui.label}>Program</label>
              <div className="flex flex-wrap gap-2.5">
                {programs.map((prog) => {
                  const active = form.program === prog;
                  return (
                    <button
                      key={prog}
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, program: prog }))}
                      className={`px-4 py-2 rounded-full border text-[13px] font-semibold transition ${
                        active
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                          : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50"
                      }`}
                    >
                      {prog}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
           HELPER — renders a single field's input control
           All onChange handlers / validation are 100% unchanged.
      ═══════════════════════════════════════════════════════════════ */}
      {(() => {
        /* shared input classes — slightly taller for tablet data-entry */
        const inp = `${ui.input} py-3 text-[14px] drawer-field-input`;
        const sel = `${ui.select} py-3 text-[14px] drawer-field-input`;

        const renderField = (field) => {
          if (adminOnlyFields.includes(field) && role !== "admin") return null;
          if (field === "religionOther") return null;

          const isRequired = !optionalFields.includes(field) && !nonRequiredFields.includes(field);

          const label = (
            <label className={`${ui.label} text-[12px]`}>
              {fieldLabels[field] || field}
              {isRequired && <span className="text-red-400 ml-1">*</span>}
            </label>
          );

          let control;

          if (field === "aadharNumber") {
            control = (
              <div className="relative">
                <input value={form.aadharNumber} onChange={handleAadharChange} maxLength={12} inputMode="numeric" placeholder="Enter 12-digit Aadhaar" className={`${inp} w-full pr-10`} />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {aadharStatus === "checking" && <CircularProgress size={16} />}
                  {aadharStatus === "available" && <CheckCircleIcon className="w-5 h-5 text-emerald-600" />}
                  {aadharStatus === "exists" && <XCircleIcon className="w-5 h-5 text-red-600" />}
                </div>
                {aadharStatus === "invalid" && <p className="text-[11px] text-red-600 font-medium mt-1">Aadhaar must be 12 digits</p>}
                {aadharStatus === "exists" && <p className="text-[11px] text-red-600 font-medium mt-1">Already registered</p>}
                {aadharStatus === "available" && <p className="text-[11px] text-emerald-600 font-medium mt-1">New Aadhaar ✓</p>}
              </div>
            );
          } else if (field === "abha") {
            control = (
              <div>
                <input value={form.abha} onChange={handleAbhaChange} maxLength={14} inputMode="numeric" placeholder="Enter 14-digit ABHA" className={inp} />
                {abhaError && <p className="text-[11px] text-red-600 font-medium mt-1">{abhaError}</p>}
              </div>
            );
          } else if (field === "contactNumber") {
            control = (
              <div>
                <input value={form.contactNumber} onChange={handleContactChange} maxLength={10} inputMode="numeric" placeholder="Enter 10-digit number" className={inp} />
                {contactError && <p className="text-[11px] text-red-600 font-medium mt-1">{contactError}</p>}
              </div>
            );
          } else if (field === "maritalStatus") {
            control = <select value={form.maritalStatus} onChange={handleChange("maritalStatus")} className={sel}><option value="">Select</option><option>Single</option><option>Married</option></select>;
          } else if (field === "annualIncome") {
            control = <select value={form.annualIncome} onChange={handleChange("annualIncome")} className={sel}><option value="">Select</option><option value="Less than 1 Lakh">Less than 1 Lakh</option><option value="1 - 4 Lakhs">1 - 4 Lakhs</option><option value="Above 4 Lakhs">Above 4 Lakhs</option></select>;
          } else if (field === "experienceYears") {
            control = <select value={form.experienceYears} onChange={handleChange("experienceYears")} className={sel}><option value="">Select</option>{[...Array(20)].map((_,i)=><option key={i+1} value={i+1}>{i+1} {i+1===1?"Year":"Years"}</option>)}</select>;
          } else if (field === "dependentFamilyMembers") {
            control = <select value={form.dependentFamilyMembers} onChange={handleChange("dependentFamilyMembers")} className={sel}><option value="">Select</option>{[...Array(10)].map((_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}</select>;
          } else if (field === "qualification") {
            control = <select value={form.qualification} onChange={handleChange("qualification")} className={sel}><option value="">Select</option><option value="Not Educated">Not Educated</option><option>5th</option><option>6th</option><option>7th</option><option>8th</option><option>9th</option><option>10th</option><option>11th</option><option>12th</option><option>Graduate</option><option value="Post Graduate">Post Graduate</option></select>;
          } else if (field === "ownerOrDriver") {
            control = <select value={form.ownerOrDriver} onChange={handleChange("ownerOrDriver")} className={sel}><option value="">Select</option><option>Owner</option><option>Driver</option></select>;
          } else if (["aadharFile", "aadharBackFile", "dlFile", "dlBackFile", "otherFile"].includes(field)) {
            const fileVal = form[field];
            const hasFile = fileVal && (typeof fileVal === "string" || (typeof fileVal === "object" && fileVal.name));
            const faceKey = field === "aadharFile" ? "aadharFace" : field === "dlFile" ? "dlFace" : null;
            const hasFace = faceKey && form[faceKey];
            const docLabel = field === "aadharFile" ? "Aadhaar Front" 
                           : field === "aadharBackFile" ? "Aadhaar Back" 
                           : field === "dlFile" ? "Driving Licence Front" 
                           : field === "dlBackFile" ? "Driving Licence Back" 
                           : "Other";
            const docIcon = ["aadharFile", "aadharBackFile"].includes(field) ? "🪪" 
                          : ["dlFile", "dlBackFile"].includes(field) ? "🚗" 
                          : "📎";
            control = (
              <div className="space-y-2">
                <div className={`doc-dropzone ${hasFile ? "has-file" : ""}`}>
                  {hasFile ? (
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{docIcon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-slate-700 truncate">
                          {typeof fileVal === "string" ? fileVal.split("/").pop() : fileVal.name}
                        </p>
                        <p className="text-[11px] text-slate-400">
                          {typeof fileVal === "string" ? "Scanned Document" : `${(fileVal.size / 1024).toFixed(1)} KB`} · {docLabel}
                        </p>
                      </div>
                      {hasFace && (
                        <span className="flex items-center gap-1 text-[10.5px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          Face auto-cropped
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-2">
                      <span className="text-3xl opacity-40">{docIcon}</span>
                      <p className="text-[12px] font-semibold text-slate-400">Click to upload {docLabel}</p>
                      <p className="text-[10px] text-slate-300">PDF, JPG, PNG up to 5MB</p>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={async (e) => {
                      const selectedFile = e.target.files[0];
                      setForm((prev) => ({ ...prev, [field]: selectedFile }));
                      if (selectedFile && ["aadharFile","dlFile"].includes(field) && selectedFile.type.startsWith("image/")) {
                        const cropToastId = toast.loading(`Cropping ${field==="aadharFile"?"Aadhaar":"DL"} face...`);
                        const cropped = await cropFaceFromImageFile(selectedFile);
                        if (cropped) {
                          const faceKey = field==="aadharFile" ? "aadharFace" : "dlFace";
                          setForm((prev) => ({ ...prev, [faceKey]: cropped }));
                          toast.success(`${field==="aadharFile"?"Aadhaar":"DL"} face cropped!`, { id: cropToastId });
                        } else { toast.dismiss(cropToastId); }
                      }
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
                {/* Scanned Aadhaar Front Preview */}
                {field === "aadharFile" && aadharPreview && (
                  <div className="mt-2 border border-emerald-200 bg-emerald-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCheckCircle className="text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700">
                        Aadhaar Front Scanned
                      </span>
                    </div>
                    <img
                      src={aadharPreview}
                      alt="Scanned Aadhaar Front"
                      className="w-full max-h-32 object-contain rounded border bg-white"
                    />
                  </div>
                )}
                {/* Scanned Aadhaar Back Preview */}
                {field === "aadharBackFile" && aadharBackPreview && (
                  <div className="mt-2 border border-emerald-200 bg-emerald-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCheckCircle className="text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700">
                        Aadhaar Back Scanned
                      </span>
                    </div>
                    <img
                      src={aadharBackPreview}
                      alt="Scanned Aadhaar Back"
                      className="w-full max-h-32 object-contain rounded border bg-white"
                    />
                  </div>
                )}
                {/* Scanned DL Front Preview */}
                {field === "dlFile" && dlPreview && (
                  <div className="mt-2 border border-emerald-200 bg-emerald-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCheckCircle className="text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700">
                        Driving Licence Front Scanned
                      </span>
                    </div>
                    <img
                      src={dlPreview}
                      alt="Scanned Driving Licence Front"
                      className="w-full max-h-32 object-contain rounded border bg-white"
                    />
                  </div>
                )}
                {/* Scanned DL Back Preview */}
                {field === "dlBackFile" && dlBackPreview && (
                  <div className="mt-2 border border-emerald-200 bg-emerald-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCheckCircle className="text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700">
                        Driving Licence Back Scanned
                      </span>
                    </div>
                    <img
                      src={dlBackPreview}
                      alt="Scanned Driving Licence Back"
                      className="w-full max-h-32 object-contain rounded border bg-white"
                    />
                  </div>
                )}
              </div>
            );
          } else if (["dob","dlIssueDate","assessmentDate","licenseExpiryDate"].includes(field)) {
            control = <input type="date" value={form[field]} onChange={handleChange(field)} className={inp} />;
          } else if (field === "gender") {
            control = <select value={form.gender} onChange={handleChange("gender")} className={sel}><option value="">Select Gender</option><option>Male</option><option>Female</option><option>Other</option></select>;
          } else if (field === "religion") {
            control = (
              <>
                <select value={form.religion} onChange={(e) => { const val=e.target.value; setForm((prev)=>({...prev,religion:val,religionOther:val==="Others"?prev.religionOther:""})); }} className={sel}>
                  <option value="">Select Religion</option>
                  <option>Hindu</option><option>Islam</option><option>Sikhism</option><option>Christianity</option><option>Buddhism</option><option>Jain</option><option>Others</option>
                </select>
                {form.religion === "Others" && (
                  <input type="text" placeholder="Enter religion" value={form.religionOther} onChange={(e)=>setForm((prev)=>({...prev,religionOther:e.target.value}))} className={`${inp} mt-2`} />
                )}
              </>
            );
          } else if (field === "caste") {
            control = <select value={form.caste} onChange={handleChange("caste")} className={sel}><option value="">Select Caste</option><option>SC</option><option>ST</option><option>OBC</option><option>NT</option><option>GEN</option></select>;
          } else if (field === "dlType") {
            control = <select value={form.dlType} onChange={handleChange("dlType")} className={sel}><option value="">Select License Type</option><option>LTV</option><option>LMV-TR</option><option>LMV-CAB</option><option>HTV</option><option>PSV</option></select>;
          } else if (field === "location") {
            control = role === "branchUser" ? (
              <input value={form.location} disabled className={`${inp} bg-slate-100 cursor-not-allowed text-slate-500`} />
            ) : (
              <select value={form.location} onChange={handleChange("location")} className={sel}>
                <option value="">Select Location</option>
                {branchLocations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            );
          } else {
            control = <input value={form[field]} onChange={handleChange(field)} placeholder={fieldLabels[field]} className={inp} />;
          }

          return { label, control };
        };

        /* ── helper functions instead of React components to prevent DOM unmounting on state change ── */
        const renderSectionCard = (id, icon, title, fields, children, sectionIndex) => {
          const { filled, total } = pct(fields);
          const complete = filled === total && total > 0;
          const meta = SECTION_META[sectionIndex] || SECTION_META[0];
          return (
            <div
              id={id}
              key={id}
              className={`${ui.card} mb-5 overflow-hidden border-l-[3px] ${meta.borderCls} drawer-section-card ${complete ? "section-complete" : ""}`}
              style={{
                animationDelay: `${sectionIndex * 40}ms`,
                "--section-accent-ring": meta.ring,
                "--section-accent-solid": meta.color,
                ...(complete ? { backgroundColor: "rgba(255,255,255,0.85)" } : {}),
              }}
            >
              {/* section header */}
              <div className={`flex items-center justify-between px-5 py-3.5 border-b border-slate-100 transition-colors duration-300 ${complete ? meta.completeBg : "bg-white"}`}>
                <div className="flex items-center gap-2.5">
                  <span className={`text-sm w-8 h-8 flex items-center justify-center rounded-xl ${meta.bgLight} ${meta.textDark}`}>
                    {icon}
                  </span>
                  <h3 className="text-[13px] font-bold text-slate-800 tracking-tight">{title}</h3>
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full transition-all ${
                  complete
                    ? `${meta.pillBg} ${meta.pillText} pill-complete-bounce`
                    : `${meta.pillBg} ${meta.pillText} opacity-70`
                }`}>
                  {filled}/{total}
                </span>
              </div>
              <div className="p-5">{children}</div>
            </div>
          );
        };

        const renderRow2 = (f1, f2) => {
          const c1 = renderField(f1);
          const c2 = f2 ? renderField(f2) : null;
          if (!c1 && !c2) return null;
          if (c1 && c2) {
            return (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <div className="flex flex-col">{c1.label}{c1.control}</div>
                <div className="flex flex-col">{c2.label}{c2.control}</div>
              </div>
            );
          }
          const active = c1 || c2;
          return (
            <div className="grid gap-4 grid-cols-1">
              <div className="flex flex-col">{active.label}{active.control}</div>
            </div>
          );
        };

        const renderFieldWrap = (f) => {
          const c = renderField(f);
          if (!c) return null;
          return <div className="flex flex-col">{c.label}{c.control}</div>;
        };

        return (
          <div>
            {/* ── 1. Personal Details ── */}
            {renderSectionCard("sec-personal", "👤", "Personal Details", ["name","fathersName","mothersName","dob","gender","maritalStatus","religion","caste"], (
              <div className="space-y-4">
                {renderRow2("name", "dob")}
                {renderRow2("fathersName", "mothersName")}
                {renderRow2("gender", "maritalStatus")}
                {renderRow2("religion", "caste")}
              </div>
            ), 0)}

            {/* ── 2. Identity & Contact ── */}
            {renderSectionCard("sec-identity", "🪪", "Identity & Contact", ["aadharNumber","contactNumber","abha","address"], (
              <div className="space-y-4">
                {renderRow2("aadharNumber", "contactNumber")}
                {renderFieldWrap("abha")}
                {renderFieldWrap("address")}
              </div>
            ), 1)}

            {/* ── 3. Driving Licence ── */}
            {renderSectionCard("sec-dl", "🚗", "Driving Licence", ["dlNo","dlIssueDate","licenseExpiryDate","dlType","dlAuthority","ownerOrDriver"], (
              <div className="space-y-4">
                {renderFieldWrap("dlNo")}
                {renderRow2("dlIssueDate", "licenseExpiryDate")}
                {renderRow2("dlType", "dlAuthority")}
                {renderFieldWrap("ownerOrDriver")}
              </div>
            ), 2)}

            {/* ── 4. Education & Employment ── */}
            {renderSectionCard("sec-edu", "🎓", "Education & Employment", ["qualification","experienceYears","employerName","employerAddress","annualIncome","dependentFamilyMembers"], (
              <div className="space-y-4">
                {renderRow2("qualification", "experienceYears")}
                {renderRow2("annualIncome", "dependentFamilyMembers")}
                {renderFieldWrap("employerName")}
                {renderFieldWrap("employerAddress")}
              </div>
            ), 3)}

            {/* ── 5. Program & Location ── */}
            {renderSectionCard("sec-program", "📍", "Program & Location", ["location","batchId","assessmentDate"], (
              <div className="space-y-4">
                {renderFieldWrap("location")}
                {renderRow2("batchId", "assessmentDate")}
              </div>
            ), 4)}

            {/* ── 6. Documents ── */}
            {renderSectionCard("sec-docs", "📄", "Documents", ["aadharFile","aadharBackFile","dlFile","dlBackFile","otherFile"], (
              <div className="space-y-4">
                {renderRow2("aadharFile", "aadharBackFile")}
                {renderRow2("dlFile", "dlBackFile")}
                {renderFieldWrap("otherFile")}
              </div>
            ), 5)}

            {/* ── 7. Admin-only fields ── */}
            {role === "admin" && renderSectionCard("sec-admin", "🏢", "Office Use Only", ["result","certificateNo","barCode","remarks","ekycRemarks","ekycRegisteredEmail","batchId"], (
              <div className="space-y-4">
                {renderRow2("result", "certificateNo")}
                {renderRow2("barCode", "ekycRegisteredEmail")}
                {renderRow2("remarks", "ekycRemarks")}
                {renderFieldWrap("vulnerability")}
              </div>
            ), 6)}
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════
           BIOMETRIC FACE VERIFICATION CARD
           (unchanged, just repositioned below sections)
      ═══════════════════════════════════════ */}
      <div className={`${ui.card} p-5 space-y-4 mb-5`}>
        <div className="flex items-center gap-2 border-b pb-3 border-slate-100">
          <FaCamera className="text-indigo-500" />
          <h3 className={ui.sectionTitle}>Biometric Face Verification</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          {/* Left Panel: Avatar Preview */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-28 h-28 rounded-full border border-indigo-200 overflow-hidden bg-indigo-50 flex items-center justify-center shadow-md">
              {form.biometricFace || capturedFaceImage ? (
                <img
                  src={form.biometricFace || capturedFaceImage}
                  alt="Biometric Avatar"
                  className="w-full h-full object-cover transform -scale-x-100"
                />
              ) : (
                <div className="flex flex-col items-center text-center text-indigo-400 space-y-1">
                  <FaCamera className="text-2xl" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">No Face Print</span>
                </div>
              )}
            </div>
            <div className="mt-3">
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={startFaceCamera}
                sx={{
                  textTransform: "none",
                  fontSize: "11px",
                  py: 0.5,
                  borderRadius: "8px",
                  fontWeight: 600
                }}
              >
                {(() => {
                  const isEnrolled = !!(form.biometricFacePy?.enrolled || form.biometricFace);
                  if (editingId) {
                    return isEnrolled ? "Re-enroll Face" : "Verify Face via Camera";
                  }
                  return capturedFaceImage ? "Re-verify Face" : "Verify Face via Camera";
                })()}
              </Button>
            </div>
          </div>
          {/* Right Panel: Biometric Status */}
          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-150 p-4 rounded-xl">
              <h4 className={ui.sectionTitle}>Biometric Status</h4>
              
              {/* Display Face Match / Verification details */}
              {liveVerificationResult && (
                <div className="mb-3 p-3 rounded-lg border bg-white shadow-sm space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Face Matching</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      liveVerificationResult.verified 
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                        : "bg-red-50 text-red-700 border border-red-200 animate-pulse"
                    }`}>
                      {liveVerificationResult.verified ? "Verified" : "Unknown Face"}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-[11.5px] font-medium text-slate-500">Match Score:</span>
                    <span className={`text-[13px] font-bold ${liveVerificationResult.verified ? "text-slate-800" : "text-red-600"}`}>
                      {liveVerificationResult.score}%
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-[11.5px] font-medium text-slate-500">Confidence:</span>
                    <span className="text-[12px] font-semibold text-slate-700">
                      {liveVerificationResult.confidence}
                    </span>
                  </div>
                  {!liveVerificationResult.verified && (
                    <p className="text-[9.5px] text-red-500 font-medium leading-normal pt-1 border-t border-red-100 mt-1">
                      ⚠️ Warning: Captured face does not match document photo. Unknown person detected.
                    </p>
                  )}
                </div>
              )}

              {(() => {
                const isEnrolled = !!(form.biometricFacePy?.enrolled || form.biometricFace);
                if (editingId) {
                  if (isEnrolled) {
                    return (
                      <div className="mt-2 space-y-2">
                        <p className="text-xs font-bold text-emerald-600 flex items-center gap-1.5">
                          <FaCheckCircle className="text-sm" /> Biometric Enrolled
                        </p>
                        <p className="text-[10px] text-slate-400 leading-normal">
                          Candidate face has been securely enrolled.
                        </p>
                        <button
                          type="button"
                          onClick={() => setFacePreviewUrl(form.biometricFace)}
                          className="mt-2 text-[10px] font-bold text-indigo-600 hover:text-indigo-800 underline block"
                        >
                          View Face
                        </button>
                      </div>
                    );
                  } else {
                    return (
                      <div className="mt-2 space-y-2">
                        <p className="text-xs font-bold text-amber-600 flex items-center gap-1.5 animate-pulse">
                          <FaExclamationTriangle className="text-sm" /> Biometric Not Enrolled
                        </p>
                        <p className="text-[10px] text-slate-400 leading-normal">
                          Capture the candidate's face to enroll biometric verification.
                        </p>
                      </div>
                    );
                  }
                } else {
                  // New Candidate Creation Mode
                  if (capturedFaceImage) {
                    return (
                      <div className="mt-2 space-y-2">
                        <p className="text-xs font-bold text-emerald-600 flex items-center gap-1.5">
                          <FaCheckCircle className="text-sm" /> Face Captured (Pending Save)
                        </p>
                        <p className="text-[10px] text-slate-400 leading-normal">
                          Face captured. Complete registration to enroll.
                        </p>
                        <div className="flex gap-3 mt-2">
                          <button
                            type="button"
                            onClick={() => setFacePreviewUrl(capturedFaceImage)}
                            className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 underline block"
                          >
                            View Face
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm("Are you sure you want to clear this face snapshot?")) {
                                setCapturedFaceImage(null);
                                setLiveVerificationResult(null);
                              }
                            }}
                            className="text-[10px] font-bold text-red-500 hover:text-red-700 underline block"
                          >
                            Clear Face Snapshot
                          </button>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="mt-2 space-y-2">
                        <p className="text-xs font-bold text-amber-600 flex items-center gap-1.5 animate-pulse">
                          <FaExclamationTriangle className="text-sm" /> Pending Verification
                        </p>
                        <p className="text-[10px] text-slate-400 leading-normal">
                          Capture the candidate's face to enroll biometric verification.
                        </p>
                      </div>
                    );
                  }
                }
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky action footer ── */}
      {(() => {
        const reqFields = getRequiredFields();
        const emptyReqFields = reqFields.filter(f => {
          const v = form[f];
          return v === undefined || v === null || String(v).trim() === "";
        });
        const fRemaining = emptyReqFields.length;
        const isIncomplete = fRemaining > 0;
        const isCreate = !editingId;

        return (
          <div className="drawer-sticky-footer">
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={closeDrawer}
                sx={{ textTransform: "none", px: 4, borderRadius: "10px", borderColor: "#e2e8f0", color: "#475569", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={saveCandidate}
                disabled={saving || (isCreate && isIncomplete)}
                title={isCreate && isIncomplete ? `${fRemaining} field${fRemaining !== 1 ? "s" : ""} remaining` : ""}
                sx={{
                  textTransform: "none", px: 4, borderRadius: "10px", fontWeight: 600, boxShadow: "none",
                  ...(isCreate && isIncomplete ? { opacity: 0.55, cursor: "not-allowed" } : {}),
                }}
              >
                {saving ? (
                  <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Saving...</>
                ) : isCreate && isIncomplete ? (
                  `Save · ${fRemaining} left`
                ) : ("Save")}
              </Button>
            </Box>
          </div>
        );
      })()}
    </Box>
    </div>{/* close rail+body flex wrapper */}
  </Drawer>

  <ExportCandidatesDialog
  open={exportDialogOpen}
  onClose={() => setExportDialogOpen(false)}
  exportType={exportType}
  locations={locations}
  candidates={data}
  defaultLocation={selectedLocation}
/>

<Dialog 
  open={confirmCreateDialog}
  onClose={() => setConfirmCreateDialog(false)}
  maxWidth="xs"
  fullWidth
  PaperProps={{ sx: { borderRadius: "18px" } }}
>
  <DialogTitle sx={{ fontWeight: 800, fontSize: 17, color: "#0f172a" }}>New Candidate Detected</DialogTitle>

  <DialogContent>
    <Typography sx={{ color: "#475569", fontSize: 13.5 }}>
      Aadhaar is not registered.
      <br />
      Do you want to create a new candidate?
    </Typography>
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2.5 }}>
    <Button
      sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}
      onClick={() => {
        setConfirmCreateDialog(false);
        toast("Candidate creation cancelled");
      }}
    >
      No
    </Button>

    <Button
      variant="contained"
      sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}
      onClick={() => {
        setConfirmCreateDialog(false);
        openCreate(ocrCandidateData);
        toast.success("Candidate data loaded from Aadhaar");
      }}
    >
      Yes
    </Button>
  </DialogActions>
</Dialog>
<Dialog
  open={aadharDialogOpen}
  onClose={() => setAadharDialogOpen(false)}
  maxWidth="xs"
  fullWidth
  PaperProps={{ sx: { borderRadius: "18px" } }}
>
  <DialogTitle sx={{ fontWeight: 800, fontSize: 17, color: "#0f172a" }}>
    Aadhaar Verification
  </DialogTitle>

  <DialogContent>
    {/* INPUT */}
    <input
      type="text"
      value={checkAadharValue}
      onChange={(e) => {
        const val = e.target.value.replace(/\D/g, "");
        if (val.length <= 12) setCheckAadharValue(val);
      }}
      placeholder="Enter 12-digit Aadhaar"
      className={`${ui.input} w-full mt-2 text-center tracking-widest`}
    />

    {checkAadharValue.length > 0 && checkAadharValue.length !== 12 && (
      <p className="text-[11.5px] text-red-600 font-medium mt-1 text-center">
        Aadhaar must be exactly 12 digits
      </p>
    )}

    {/* STATUS */}
    <Box mt={3} textAlign="center">
      {aadharCheckStatus === "checking" && (
        <>
          <CircularProgress size={22} />
          <p className="text-sm mt-2 text-slate-600 font-medium">Checking Aadhaar…</p>
        </>
      )}

      {aadharCheckStatus === "exists" && (
        <p className="text-red-600 font-semibold">
          Aadhaar already registered
        </p>
      )}

      {aadharCheckStatus === "new" && (
        <p className="text-emerald-600 font-semibold">
          New Aadhaar detected
        </p>
      )}
    </Box>
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 3 }}>
    <Button sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }} onClick={() => setAadharDialogOpen(false)}>
      Cancel
    </Button>

    {aadharCheckStatus === "exists" ? (
      <Button
        variant="outlined"
        sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}
        onClick={() => {
          setAadharDialogOpen(false);
          setViewDialogOpen(true);
        }}
      >
        View Candidate
      </Button>
    ) : (
      <Button
        variant="contained"
        sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}
        disabled={checkAadharValue.length !== 12}
        onClick={async () => {
          setAadharCheckStatus("checking");
          const res = await checkAadhar(checkAadharValue);

          if (res.data.exists) {
            setSelectedCandidate(res.data.candidate);
            setAadharCheckStatus("exists");
          } else {
            setForm((prev) => ({
              ...prev,
              aadharNumber: checkAadharValue,
            }));
            setAadharCheckStatus("new");
          }
        }}
      >
        Check
      </Button>
    )}
  </DialogActions>
</Dialog>


<Dialog
  open={approvalDialog}
  onClose={() => setApprovalDialog(false)}
  maxWidth="sm"
  fullWidth
  PaperProps={{ sx: { borderRadius: "18px" } }}
>
  <DialogTitle sx={{ fontWeight: 800, fontSize: 17, color: "#0f172a" }}>Approve / Reject Candidate</DialogTitle>

  <DialogContent>
    <Typography sx={{ fontWeight: 700, color: "#1e293b" }}>
      {approvalCandidate?.name}
    </Typography>

    <Box mt={3}>
      <select
        value={approvalStatus}
        onChange={(e) => setApprovalStatus(e.target.value)}
        className={`${ui.select} w-full`}
      >
        <option value="">Select Action</option>
        <option value="Approved">Approve</option>
        <option value="Rejected">Reject</option>
      </select>
    </Box>

    {approvalStatus === "Rejected" && (
      <Box mt={2}>
        <textarea
          value={approvalReason}
          onChange={(e) => setApprovalReason(e.target.value)}
          placeholder="Enter rejection reason"
          className={`${ui.input} w-full`}
          rows={3}
        />
      </Box>
    )}
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2.5 }}>
    <Button sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }} onClick={() => setApprovalDialog(false)}>
      Cancel
    </Button>

    <Button
      variant="contained"
      sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}
      disabled={
        !approvalStatus ||
        (approvalStatus === "Rejected" && !approvalReason)
      }
      onClick={async () => {
      await approveCandidate(approvalCandidate._id, {
  approvalstatus: approvalStatus,
  approvalReason,
});


        toast.success(`Candidate ${approvalStatus}`);
        setApprovalDialog(false);
        fetchCandidates();
      }}
    >
      Submit
    </Button>
  </DialogActions>
</Dialog>

<Dialog
  open={csvDialogOpen}
  onClose={csvUploading ? null : resetCsvUpload}
  maxWidth="md"
  fullWidth
  PaperProps={{ sx: { borderRadius: "18px" } }}
>
  <DialogTitle sx={{ fontWeight: 800, fontSize: 17, color: "#0f172a", display: 'flex', alignItems: 'center', gap: 1 }}>
    <DocumentArrowDownIcon className="w-6 h-6" />
    Bulk Upload Candidates (CSV)
  </DialogTitle>

  <DialogContent dividers>
    <Box sx={{ space: 2 }}>
      {/* INSTRUCTIONS */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#eef2ff', borderRadius: '14px' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
          CSV Format Requirements
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: "#475569" }}>
          Your CSV file must include these columns:
        </Typography>
        <div className="bg-white p-2 rounded-lg font-mono text-xs border border-indigo-200 mb-2">
          <p>Project, Aadharno</p>
        </div>
        <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
          Project values like "Linde Hazmat", "Linde Upskilling", and "SSSF Upskilling" are automatically mapped to the correct project and program.
        </Typography>
        <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
          Aadharno must contain a valid 12 digit Aadhaar number. Invalid rows will be skipped.
        </Typography>
      </Box>

      {/* FILE UPLOAD */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
          Select CSV File
        </Typography>
        <input
          type="file"
          accept=".csv"
          onChange={handleCsvFileChange}
          disabled={csvUploading}
          className={`${ui.input} w-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
        />
        <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, display: 'block' }}>
          Maximum file size: 5MB
        </Typography>
      </Box>

      {/* FILE INFO */}
      {csvFile && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
            File Selected
          </Typography>
          <Typography variant="body2" sx={{ color: "#475569" }}>
            <strong>Name:</strong> {csvFile.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "#475569" }}>
            <strong>Size:</strong> {(csvFile.size / 1024).toFixed(2)} KB
          </Typography>
        </Box>
      )}

      {/* CSV PREVIEW */}
      {csvPreview.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
            Preview (First 5 rows)
          </Typography>
          <Box
            sx={{
              bgcolor: '#f8fafc',
              p: 2,
              borderRadius: '14px',
              overflowX: 'auto',
              border: '1px solid',
              borderColor: '#e2e8f0'
            }}
          >
            <pre className="text-xs whitespace-pre-wrap break-words font-mono">
              {csvPreview.join('\n')}
            </pre>
          </Box>
        </Box>
      )}

      {/* UPLOAD RESULT */}
      {csvResult && (
        <Box
          sx={{
            p: 3,
            borderRadius: '18px',
            bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }} mb={2}>
            📊 Upload Summary
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
            }}
          >
            <Box className="p-3 rounded-xl bg-slate-100">
              <p className="text-xs text-slate-500 font-medium">Total Rows</p>
              <p className="font-bold text-lg text-slate-800">{csvResult.totalRows}</p>
            </Box>

            <Box className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
              <p className="text-xs text-emerald-700 font-medium">Inserted</p>
              <p className="font-bold text-lg text-emerald-700">
                {csvResult.inserted}
              </p>
            </Box>

            <Box className="p-3 rounded-xl bg-amber-50 border border-amber-100">
              <p className="text-xs text-amber-700 font-medium">Skipped</p>
              <p className="font-bold text-lg text-amber-700">
                {csvResult.skipped}
              </p>
            </Box>

            <Box className="p-3 rounded-xl bg-red-50 border border-red-100">
              <p className="text-xs text-red-700 font-medium">Duplicates</p>
              <p className="font-bold text-lg text-red-700">
                {csvResult.duplicates || 0}
              </p>
            </Box>
          </Box>
          {csvResult.duplicates > 0 && (
            <div className="mt-2 text-red-600 text-sm font-semibold">
              ⚠ {csvResult.duplicates} duplicate records found
            </div>
          )}

          {csvResult?.errors?.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: '14px',
                bgcolor: "#fff1f2",
                border: "1px solid #fecaca",
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              <Typography sx={{ fontWeight: 700 }} color="error">
                ⚠ Duplicate / Error Details
              </Typography>
              {csvResult.errors.map((err, i) => (
                <Typography key={i} variant="caption" display="block">
                  •{" "}
                  {typeof err === "string"
                    ? err
                    : `Row ${err.row || i + 1} - ${err.reason || "Error"}`}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* LOADING STATE */}
      {csvUploading && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography sx={{ color: "#64748b", fontWeight: 500 }}>Uploading and processing CSV...</Typography>
        </Box>
      )}
    </Box>
  </DialogContent>

  <DialogActions sx={{ p: 2, gap: 1 }}>
    <Button
      sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}
      onClick={resetCsvUpload}
      disabled={csvUploading}
    >
      {csvResult ? 'Close' : 'Cancel'}
    </Button>
    {!csvResult && (
      <Button
        variant="contained"
        color="primary"
        sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}
        onClick={handleCsvUpload}
        disabled={!csvFile || csvUploading}
        startIcon={csvUploading ? <CircularProgress size={20} /> : null}
      >
        {csvUploading ? 'Uploading...' : 'Upload CSV'}
      </Button>
    )}
    {csvResult && (
      <Button
        variant="contained"
        color="success"
        sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}
        onClick={resetCsvUpload}
      >
        Done
      </Button>
    )}
  </DialogActions>
</Dialog>
<ViewCandidateModal
  open={viewDialogOpen}
  onClose={() => setViewDialogOpen(false)}
  selectedCandidate={selectedCandidate}
/>

{/* =====================================================================
    CREATE CANDIDATE SELECTION DIALOG (SCAN OR MANUAL)
   ===================================================================== */}
<Dialog
  open={createCandidateDialogOpen}
  onClose={() => setCreateCandidateDialogOpen(false)}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: "20px",
      p: 1.5,
      boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.15)",
    },
  }}
>
  <DialogTitle sx={{ pb: 1, pr: 6 }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.25rem", tracking: "-0.02em" }}>
          Create Candidate
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, fontSize: "0.875rem" }}>
          Choose how you want to start the registration.
        </Typography>
      </Box>
    </Box>
    <button
      onClick={() => setCreateCandidateDialogOpen(false)}
      className="absolute top-5 right-5 w-8 h-8 rounded-full bg-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition flex items-center justify-center cursor-pointer"
    >
      <XMarkIcon className="w-5 h-5" />
    </button>
  </DialogTitle>

  <DialogContent sx={{ py: 2 }}>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* OPTION 1: SCAN AADHAAR & DL */}
      <div
        onClick={handleStartScanRegistration}
        className="group relative bg-white border border-slate-200 hover:border-[#2BB5CE] rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg flex flex-col justify-between"
      >
        <div>
          <div className="w-12 h-12 rounded-xl bg-[#2BB5CE]/10 text-[#2BB5CE] flex items-center justify-center mb-4 transition-transform duration-200 group-hover:scale-110">
            <FaCamera className="text-xl" />
          </div>
          <h4 className="text-base font-bold text-slate-800 mb-1.5 group-hover:text-[#2BB5CE] transition-colors">
            Scan Aadhaar &amp; DL
          </h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6">
            Scan Aadhaar and Driving Licence to automatically extract candidate details and continue with registration.
          </p>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleStartScanRegistration();
          }}
          className="w-full h-10 rounded-xl bg-[#2BB5CE] text-white font-semibold text-xs shadow-sm hover:bg-[#1A869B] transition active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <CameraIcon className="w-4 h-4" />
          <span>Scan Aadhaar &amp; DL</span>
        </button>
      </div>

      {/* OPTION 2: MANUAL REGISTRATION */}
      <div
        onClick={handleStartManualRegistration}
        className="group relative bg-white border border-slate-200 hover:border-slate-400 rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg flex flex-col justify-between"
      >
        <div>
          <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center mb-4 transition-transform duration-200 group-hover:scale-110">
            <BiEdit className="text-2xl" />
          </div>
          <h4 className="text-base font-bold text-slate-800 mb-1.5 group-hover:text-slate-900 transition-colors">
            Manual Registration
          </h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6">
            Enter candidate details manually without scanning Aadhaar or Driving Licence.
          </p>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleStartManualRegistration();
          }}
          className="w-full h-10 rounded-xl bg-slate-900 text-white font-semibold text-xs shadow-sm hover:bg-slate-800 transition active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <BiEdit className="text-base" />
          <span>Manual Registration</span>
        </button>
      </div>
    </div>
  </DialogContent>
</Dialog>


        {/* DELETE CONFIRM */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: "18px" } }}>
          <DialogTitle sx={{ fontWeight: 800, fontSize: 17, color: "#0f172a" }}>Delete Candidate?</DialogTitle>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }} onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }} onClick={performDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* FACE SNAPSHOT PREVIEW DIALOG */}
        <Dialog
          open={Boolean(facePreviewUrl)}
          onClose={() => setFacePreviewUrl(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
        >
          <DialogTitle className="flex justify-between items-center pb-2 border-b border-gray-150 text-slate-800 font-extrabold text-sm uppercase tracking-wider">
            <span>Biometric Face Print</span>
            <button className="text-slate-400 hover:text-slate-600 text-lg cursor-pointer" onClick={() => setFacePreviewUrl(null)}>×</button>
          </DialogTitle>
          <DialogContent className="p-6 bg-slate-50 flex flex-col items-center gap-4">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-sm">
              High Resolution Biometric Capture Preview
            </span>
            <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-md max-w-full max-h-[70vh] flex items-center justify-center">
              <img
                src={facePreviewUrl}
                alt="Biometric Capture Preview"
                className="max-w-full max-h-[60vh] object-contain transform -scale-x-100"
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* FACE CAMERA CAPTURE / SCAN DIALOG */}
        <Dialog
          open={faceCameraOpen}
          onClose={stopFaceCamera}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "18px", p: 1 } }}
        >
          <DialogTitle className="flex justify-between items-center pb-3 border-b border-slate-150">
            <div>
              <h3 className="text-lg font-bold text-slate-800 uppercase flex items-center gap-2">
                <FaCamera className="text-indigo-600 animate-pulse" />
                Biometric Face Capture & Scan
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 font-medium">
                Position candidate's face in the camera viewport below and scan.
              </p>
            </div>
            <button className="text-slate-400 hover:text-slate-600 text-lg cursor-pointer font-bold" onClick={stopFaceCamera}>×</button>
          </DialogTitle>
          <DialogContent className="p-6 bg-slate-50 flex flex-col items-center gap-6" dividers>
            <div className="relative w-full max-w-[480px] aspect-[4/3] bg-slate-900 rounded-2xl overflow-hidden shadow-lg border border-slate-800 flex items-center justify-center">
              <video
                id="face-video-feed"
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover transform -scale-x-100 ${faceScanning ? 'opacity-45' : 'opacity-100'}`}
              />
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-36 h-36 border-2 border-dashed border-indigo-400 rounded-full animate-pulse flex items-center justify-center">
                  {faceScanning && (
                    <div className="w-32 h-32 border border-indigo-400/40 rounded-full animate-ping"></div>
                  )}
                </div>
                <div className="w-full h-0.5 bg-indigo-400 absolute top-0 left-0 animate-[bounce_2s_infinite] shadow-[0_0_15px_#818cf8]"></div>
              </div>
              {faceScanning && (
                <div className="absolute top-3 left-3 bg-indigo-900/80 backdrop-blur-md px-3 py-1 rounded-full border border-indigo-500/30 text-indigo-200 text-xs font-bold uppercase tracking-wider animate-pulse z-10 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-indigo-300 rounded-full animate-ping"></span>
                  Scanning Face: {faceScanProgress}%
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button
                variant="contained"
                color="primary"
                onClick={captureFaceImage}
                disabled={faceScanning}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  borderRadius: "10px",
                  boxShadow: "none"
                }}
              >
                {faceScanning ? "Processing..." : "Scan & Verify Face"}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={stopFaceCamera}
                disabled={faceScanning}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  borderRadius: "10px"
                }}
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ATTENDANCE LOG CHECKLIST MODAL (MORE THAN 1 DAY) */}
        <Dialog
          open={attendanceLogOpen}
          onClose={() => setAttendanceLogOpen(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: { borderRadius: "18px", p: 1 }
          }}
        >
          <DialogTitle className="flex justify-between items-center border-b pb-3">
            <div>
              <h3 className="text-lg font-bold text-slate-800 uppercase flex items-center gap-2">
                <FaCalendarCheck className="text-indigo-600" />
                Attendance Verification Logs
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 font-medium">
                Biometric verification timestamps and captured live proof photos for {selectedLogCandidate?.name?.toUpperCase()}.
              </p>
            </div>
          </DialogTitle>
          <DialogContent className="py-4" dividers>
            {selectedLogRecords.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                <FaCalendarCheck className="text-4xl text-slate-300 mb-2 animate-bounce" />
                <p className="text-sm font-semibold">No Attendance Logs Found</p>
                <p className="text-xs text-slate-400 mt-0.5">This candidate has not verified their attendance yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {selectedLogRecords.map((record) => (
                  <div key={record.day} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
                    {/* Photo Area */}
                    <div className="h-44 bg-slate-900 relative flex items-center justify-center overflow-hidden border-b">
                      {record.photo ? (
                        <img
                          src={record.photo}
                          alt={`Day ${record.day} Verification`}
                          className="w-full h-full object-cover transform -scale-x-100"
                        />
                      ) : (
                        <div className="text-slate-500 text-xs flex flex-col items-center">
                          <FaCamera className="text-2xl text-slate-600 mb-1" />
                          <span>No Live Photo Captured</span>
                        </div>
                      )}
                      <div className="absolute top-2 left-2 bg-indigo-600 text-white font-bold text-[10px] px-2 py-0.5 rounded-full shadow">
                        Day {record.day}
                      </div>
                    </div>
                    {/* Timing details */}
                    <div className="p-3 bg-white flex-1 flex flex-col justify-center">
                      <span className="text-[10px] uppercase font-bold text-slate-400">Verified Timestamp</span>
                      <span className="text-xs font-bold text-slate-700 mt-0.5 block select-text">
                        {record.verifiedAt 
                          ? new Date(record.verifiedAt).toLocaleDateString([], { dateStyle: 'medium' })
                          : "Legacy Record"
                        }
                      </span>
                      <span className="text-[10px] text-slate-500 mt-0.5 font-medium block">
                        {record.verifiedAt 
                          ? new Date(record.verifiedAt).toLocaleTimeString([], { timeStyle: 'short' })
                          : "—"
                        }
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }} onClick={() => setAttendanceLogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

      </Box>
    );
  };

  export default Candidates;